<template>
  <div>
    <header-bar>
      {{ type === 'album' ? 'アルバム（2曲以上）' : 'シングル' }}配信
    </header-bar>
    <select-main-artist
      :is-shown="popUpMainArtistDialogShown"
      :selected-ids="selectedMainArtistIds"
      :excluded-ids="excludedMainArtistIds"
      @close="closeMainArtistModal"
      @select="onMainArtistSelect" />
    <select-featuring-artist
      :is-shown="popUpFeaturingArtistDialogShown"
      :selected-ids="selectedFeaturingArtistIds"
      :excluded-ids="excludedFeaturingArtistIds"
      @close="closeFeaturingArtistModal"
      @select="onFeaturingArtistSelect" />
    <select-copyright-artist
      :is-shown="popUpCopyrightArtistDialogShown"
      :selected-ids="selectedCopyrightArtistIds"
      @close="closeCopyrightArtistModal"
      @select="onCopyrightArtistSelect" />
    <select-role-assignment-artist
      :is-shown="roleAssignmentModal.isShown"
      :selected-ids="roleAssignmentSelectedIds"
      @close="closeRoleAssignmentModal"
      @select="onRoleAssignmentArtistSelect" />
    <role-selection-dialog
      :is-shown="performanceRoleModal.isShown"
      :selected-ids="performanceRoleModal.selectedIds"
      :roles="performanceRoleCatalog"
      :title="'パフォーマンスの役割を選択'"
      modal-name="performanceRoleSelectionModal"
      @close="closePerformanceRoleModal"
      @select="onPerformanceRoleSelect" />
    <role-selection-dialog
      :is-shown="productionRoleModal.isShown"
      :selected-ids="productionRoleModal.selectedIds"
      :roles="productionRoleCatalog"
      :title="'プロダクション＆エンジニアリングの役割を選択'"
      modal-name="productionRoleSelectionModal"
      @close="closeProductionRoleModal"
      @select="onProductionRoleSelect" />
    <div class="page-block">
      <div class="page-block__heading page-block__heading--withbar">
        <h3 class="page-block__title">リリース情報登録 - 楽曲情報の登録</h3>
        <release-type-label :type="type" />
      </div>
      <div class="page-block__inner">
        <release-step-area :index="2">
          楽曲情報の登録
        </release-step-area>
        <alert-view
          v-if="!realtimeValidation"
          :errors="errors" />
        <form
          class="form--error"
          @submit.prevent="onRegisterClicked()">
          <div class="form-wrap">
            <div class="form-group">
              <div class="form-label">
                <label class="control-label">楽曲区分
                  <span class="req">＊</span>
                </label>
                <i
                  v-tooltip="spTooltipConfig(tooltipMessages.classification)"
                  class="sp-tooltip-icon">
                </i>
                <i
                  v-tooltip="pcTooltipConfig(tooltipMessages.classification)"
                  class="tooltip-icon">
                </i>
              </div>
              <div class="form-item">
                <div class="form-item-inline">
                  <div class="radio">
                    <label for="classification_original">
                      <input
                        id="classification_original"
                        v-model="item.classification"
                        type="radio"
                        name="classification"
                        :value="1"
                        :disabled="disableTitleChange"
                        checked
                        @change="onChangeClassification(1)">
                      <div class="option-input"></div>
                      オリジナル
                    </label>
                  </div>
                  <div class="radio">
                    <label for="classification_cover">
                      <input
                        id="classification_cover"
                        v-model="item.classification"
                        type="radio"
                        name="classification"
                        :disabled="disableTitleChange"
                        :value="2"
                        @change="onChangeClassification(2)">
                      <div class="option-input"></div>
                      カバー
                    </label>
                  </div>
                  <div class="radio">
                    <label for="classification_arrange">
                      <input
                        id="classification_arrange"
                        v-model="item.classification"
                        type="radio"
                        name="classification"
                        :disabled="disableTitleChange"
                        :value="3"
                        @change="onChangeClassification(3)">
                      <div class="option-input"></div>
                      アレンジ
                    </label>
                  </div>
                  <div class="radio">
                    <label for="classification_public">
                      <input
                        id="classification_public"
                        v-model="item.classification"
                        type="radio"
                        name="classification"
                        :disabled="disableTitleChange"
                        :value="4"
                        @change="onChangeClassification(4)">
                      <div class="option-input"></div>
                      パブリックドメイン
                    </label>
                  </div>
                </div>
              </div>
            </div>
            <div class="form-group">
              <div class="form-label">
                <label
                  class="control-label"
                  for="title_ja">タイトル
                  <span class="req">＊</span>
                </label>
                <i
                  v-tooltip="spTooltipConfig(tooltipMessages.title_ja)"
                  class="sp-tooltip-icon">
                </i>
              </div>
              <div class="form-item">
                <input
                  id="title_ja"
                  v-model="item.title_ja"
                  v-tooltip="pcTooltipConfig(tooltipMessages.title_ja)"
                  class="form-control"
                  type="text"
                  :disabled="disableTitleChange"
                  @blur="onFieldChanged('title_ja')"
                  @input="findVersioWords()">
                <p
                  v-if="foundVersioWords"
                  class="error-text">
                  バージョン(Ver. / Remix / 2022 Remastered / Cover / Live 等)はタイトル欄では無くバージョン欄に登録してください。
                </p>
                <p
                  v-if="errors.title_ja"
                  class="error-text">
                  {{ errors.title_ja[0] }}
                </p>
                <p
                  v-if="hasInitialDigit"
                  class="notice-text">
                  タイトルの頭に数字が入っている場合は、トラック番号とみなされ変更が入る場合がございます。
                </p>
              </div>
            </div>
            <div class="form-group">
              <div class="form-label">
                <label
                  class="control-label"
                  for="title_kana">タイトル（カナ表記）
                  <span class="req">＊</span>
                </label>
                <i
                  v-tooltip="spTooltipConfig(tooltipMessages.title_kana)"
                  class="sp-tooltip-icon">
                </i>
              </div>
              <div class="form-item">
                <input
                  id="title_kana"
                  v-model="item.title_kana"
                  v-tooltip="pcTooltipConfig(tooltipMessages.title_kana)"
                  class="form-control"
                  type="text"
                  :disabled="disableTitleChange"
                  @blur="onFieldChanged('title_kana')">
                <p
                  v-if="errors.title_kana"
                  class="error-text">
                  {{ errors.title_kana[0] }}
                </p>
              </div>
            </div>
            <div class="form-group">
              <div class="form-label">
                <label
                  class="control-label"
                  for="title_ja">タイトル（アルファベット表記）
                  <span class="req">＊</span>
                </label>
                <i
                  v-tooltip="spTooltipConfig(tooltipMessages.title_en)"
                  class="sp-tooltip-icon">
                </i>
              </div>
              <div class="form-item">
                <input
                  id="title_en"
                  v-model="item.title_en"
                  v-tooltip="pcTooltipConfig(tooltipMessages.title_en)"
                  class="form-control"
                  type="text"
                  :disabled="disableTitleChange"
                  @blur="onFieldChanged('title_en')">
                <p
                  v-if="errors.title_en"
                  class="error-text">
                  {{ errors.title_en[0] }}
                </p>
              </div>
            </div>
            <div
              v-if="!hideVersion"
              class="form-group-wrap">
              <div class="form-group-wrap__version-title js-tooltip">
                バージョン
                <i
                  v-tooltip="spTooltipConfig(
                    isBtoB ? tooltipMessages.versions_b2b : tooltipMessages.versions_b2c
                  )"
                  class="sp-tooltip-icon">
                </i>
                <i
                  v-tooltip="pcTooltipConfig(
                    isBtoB ? tooltipMessages.versions_b2b : tooltipMessages.versions_b2c
                  )"
                  class="tooltip-icon">
                </i>
              </div>
              <div class="form-group-wrap__example">
                [例]： Live / Remix / 2022 Remastered / Cover / Ver. など
                <div
                  class="form-item-text"
                  style="margin-top:1em;">
                  オリジナル版と区別をするため、バージョンをご登録いただく必要がございます。
                  <br><br>
                  バージョンを登録した楽曲は、著作権管理サービスを利用できません。詳しくは<a
                    href="/client/faqs/179"
                    class="c-text-link"
                    target="_blank">こちら</a>
                </div>
              </div>
              <div
                v-if="version_status">
                <div class="form-group">
                  <div class="form-label">
                    <label
                      class="control-label"
                      for="version_ja">バージョン
                    </label>
                    <i
                      v-tooltip="spTooltipConfig(tooltipMessages.version_ja)"
                      class="sp-tooltip-icon">
                    </i>
                  </div>
                  <div class="form-item">
                    <input
                      id="version_ja"
                      v-model="item.version_ja"
                      v-tooltip="pcTooltipConfig(tooltipMessages.version_ja)"
                      class="form-control"
                      type="text"
                      placeholder="(例)Acoustic. Ver"
                      :disabled="disableTitleChange"
                      @blur="onFieldChanged(['title_ja', 'version_ja'])">
                    <div
                      v-if="errors.version_ja"
                      class="error-text">
                      <p
                        v-for="message in errors.version_ja[0].split('\n')"
                        :key="message">
                        {{ message }}
                      </p>
                    </div>
                  </div>
                </div>
                <div class="form-group">
                  <div class="form-label">
                    <label
                      class="control-label"
                      for="version_kana">バージョン（カナ表記）
                    </label>
                    <i
                      v-tooltip="spTooltipConfig(tooltipMessages.version_kana)"
                      class="sp-tooltip-icon">
                    </i>
                  </div>
                  <div class="form-item">
                    <input
                      id="version_kana"
                      v-model="item.version_kana"
                      v-tooltip="pcTooltipConfig(tooltipMessages.version_kana)"
                      class="form-control"
                      type="text"
                      placeholder="(例)アコースティックバージョン"
                      :disabled="disableTitleChange"
                      @blur="onFieldChanged(['title_kana', 'version_kana'])">
                    <p
                      v-if="errors.version_kana"
                      class="error-text">
                      {{ errors.version_kana[0] }}
                    </p>
                  </div>
                </div>
                <div class="form-group">
                  <div class="form-label">
                    <label
                      class="control-label"
                      for="title_en">バージョン（アルファベット表記）
                    </label>
                    <i
                      v-tooltip="spTooltipConfig(tooltipMessages.version_en)"
                      class="sp-tooltip-icon">
                    </i>
                  </div>
                  <div class="form-item">
                    <input
                      id="version_en"
                      v-model="item.version_en"
                      v-tooltip="pcTooltipConfig(tooltipMessages.version_en)"
                      class="form-control"
                      type="text"
                      placeholder="(例)Acoustic. Ver"
                      :disabled="disableTitleChange"
                      @blur="onFieldChanged(['title_en', 'version_en'])">
                    <div
                      v-if="errors.version_en"
                      class="error-text">
                      <p
                        v-for="message in errors.version_en[0].split('\n')"
                        :key="message">
                        {{ message }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div
                v-if="!disableTitleChange"
                class="form-group-wrap__btn-wrap">
                <button
                  v-if="!version_status"
                  type="button"
                  class="c-btn c-btn--narrow"
                  @click.prevent="onVersionAddClicked">
                  追加
                  <span class="c-btn__icon-add">
                    <span></span>
                  </span>
                </button>
                <button
                  v-if="version_status"
                  type="button"
                  class="c-btn c-btn--delete-large c-btn--narrow c-btn--right"
                  @click.prevent="onVersionDeleteClicked">
                  削除
                </button>
              </div>
            </div>
            <div class="form-group-wrap">
              <div class="form-group-wrap__title">メインアーティスト</div>
              <p
                v-if="errors['main_artists']"
                class="error-text">
                {{ errors['main_artists'][0] }}
              </p>
              <div
                v-for="(artist, index) in item.main_artists"
                :key="index">
                <div v-if="artist.type !==3">
                  <div
                    :class="{ 'not-first-space': index === 0 }"
                    class="form-group form-group--set">
                    <div class="form-label">
                      <label
                        class="control-label"
                        :for="`main_artist_name_ja_${index}`">アーティスト名
                        <span class="req">＊</span>
                      </label>
                      <i
                        v-tooltip="spTooltipConfig(tooltipMessages.main_artist_name_ja)"
                        class="sp-tooltip-icon">
                      </i>
                    </div>
                    <div class="form-item">
                      <input
                        :id="`main_artist_name_ja_${index}`"
                        v-model="artist.name_ja"
                        class="form-control"
                        type="text"
                        :disabled="!!artist.id"
                        @blur="onFieldChanged(`main_artists.${index}.name_ja`)">
                      <p
                        v-if="errors[`main_artists.${index}.name_ja`]"
                        class="error-text">
                        {{ errors[`main_artists.${index}.name_ja`][0] }}
                      </p>
                      <p
                        v-else-if="mainArtistWarnErrors[`main_artists.${index}.name_ja`]"
                        class="error-text">
                        {{ mainArtistWarnErrors[`main_artists.${index}.name_ja`] }}
                      </p>
                    </div>
                  </div>
                  <div class="form-group form-group--set">
                    <div class="form-label">
                      <label
                        class="control-label"
                        :for="`main_artist_name_kana_${index}`">アーティスト名（カナ表記）
                        <span class="req">＊</span>
                      </label>
                    </div>
                    <div class="form-item">
                      <input
                        :id="`main_artist_name_kana_${index}`"
                        v-model="artist.name_kana"
                        class="form-control"
                        type="text"
                        :disabled="!!artist.id"
                        @blur="onFieldChanged(`main_artists.${index}.name_kana`)">
                      <p
                        v-if="errors[`main_artists.${index}.name_kana`]"
                        class="error-text">
                        {{ errors[`main_artists.${index}.name_kana`][0] }}
                      </p>
                    </div>
                  </div>
                  <div class="form-group form-group--set">
                    <div class="form-label">
                      <label
                        class="control-label"
                        :for="`main_artist_name_en_${index}`">アーティスト名（アルファベット表記）
                        <span class="req">＊</span>
                      </label>
                    </div>
                    <div class="form-item">
                      <input
                        :id="`main_artist_name_en_${index}`"
                        v-model="artist.name_en"
                        class="form-control"
                        type="text"
                        :disabled="!!artist.id"
                        @blur="onFieldChanged(`main_artists.${index}.name_en`)">
                      <p
                        v-if="errors[`main_artists.${index}.name_en`]"
                        class="error-text">
                        {{ errors[`main_artists.${index}.name_en`][0] }}
                      </p>
                    </div>
                  </div>
                </div>
                <div
                  v-else
                  class="vocaloid-image-input">
                  <div class="vocaloid-image-container">
                    <img
                      :src="`/images/${artist.image_path}`" />
                  </div>
                  <div class="vocaloid-input">
                    <div
                      :class="{ 'not-first-space': index === 0 }"
                      class="form-group form-group--set">
                      <div class="form-label">
                        <label
                          class="control-label"
                          :for="`main_artist_name_ja_${index}`">ボーカロイド名
                          <span class="req">＊</span>
                        </label>
                        <i
                          v-tooltip="spTooltipConfig(tooltipMessages.main_artist_name_ja)"
                          class="sp-tooltip-icon">
                        </i>
                      </div>
                      <div class="form-item">
                        <input
                          :id="`main_artist_name_ja_${index}`"
                          v-model="artist.name_ja"
                          v-tooltip="pcTooltipConfig(tooltipMessages.main_artist_name_ja)"
                          class="form-control"
                          type="text"
                          :disabled="true"
                          @blur="onFieldChanged(`main_artists.${index}.name_ja`)">
                        <p
                          v-if="errors[`main_artists.${index}.name_ja`]"
                          class="error-text">
                          {{ errors[`main_artists.${index}.name_ja`][0] }}
                        </p>
                      </div>
                    </div>
                    <div class="form-group form-group--set">
                      <div class="form-label">
                        <label
                          class="control-label"
                          :for="`main_artist_name_kana_${index}`">ボーカロイド名（カナ表記）
                          <span class="req">＊</span>
                        </label>
                        <i
                          v-tooltip="spTooltipConfig(tooltipMessages.main_artist_name_kana)"
                          class="sp-tooltip-icon">
                        </i>
                      </div>
                      <div class="form-item">
                        <input
                          :id="`main_artist_name_kana_${index}`"
                          v-model="artist.name_kana"
                          v-tooltip="pcTooltipConfig(tooltipMessages.main_artist_name_kana)"
                          class="form-control"
                          type="text"
                          :disabled="true"
                          @blur="onFieldChanged(`main_artists.${index}.name_kana`)">
                        <p
                          v-if="errors[`main_artists.${index}.name_kana`]"
                          class="error-text">
                          {{ errors[`main_artists.${index}.name_kana`][0] }}
                        </p>
                      </div>
                    </div>
                    <div class="form-group form-group--set">
                      <div class="form-label">
                        <label
                          class="control-label"
                          :for="`main_artist_name_en_${index}`">ボーカロイド名（アルファベット表記)
                          <span class="req">＊</span>
                        </label>
                        <i
                          v-tooltip="spTooltipConfig(tooltipMessages.main_artist_name_en)"
                          class="sp-tooltip-icon">
                        </i>
                      </div>
                      <div class="form-item">
                        <input
                          :id="`main_artist_name_en_${index}`"
                          v-model="artist.name_en"
                          v-tooltip="pcTooltipConfig(tooltipMessages.main_artist_name_en)"
                          class="form-control"
                          type="text"
                          :disabled="true"
                          @blur="onFieldChanged(`main_artists.${index}.name_en`)">
                        <p
                          v-if="errors[`main_artists.${index}.name_en`]"
                          class="error-text">
                          {{ errors[`main_artists.${index}.name_en`][0] }}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-if="!(type === 'single' && artistType !== 2)"
                  class="form-group-wrap__btn-wrap form-group-wrap__vocalo-btn-wrap">
                  <button
                    v-if="item.main_artists.length - 1 === index
                      && item.main_artists.length < maxMainArtists"
                    type="button"
                    class="c-btn c-btn--narrow"
                    @click.prevent="showMainArtistPopUp()">
                    マイアーティストから追加
                    <span class="c-btn__icon-add">
                      <span></span>
                    </span>
                  </button>
                  <button
                    v-if="item.main_artists.length - 1 === index
                      && item.main_artists.length < maxMainArtists"
                    type="button"
                    class="c-btn c-btn--narrow"
                    @click.prevent="showVocaloidListPopUp()">
                    ボーカロイド/その他音声合成ソフトを追加
                    <span class="c-btn__icon-add">
                      <span></span>
                    </span>
                  </button>
                  <button
                    v-if="isShowMainArtistDeleteButton(artist.id)"
                    type="button"
                    class="c-btn c-btn--delete-large c-btn--narrow c-btn--right"
                    style="margin-bottom: 10px;clear-after: both"
                    @click.prevent="artist.type === 3? onVocaloidDeleteClicked(index,artist)
                      :onMainArtistDeleteClicked(index)">
                    削除
                  </button>
                </div>
              </div>
              <div
                v-if="!(type === 'single' && artistType !== 2)"
                class="form-group-wrap__btn-wrap  form-group-wrap__vocalo-btn-wrap">
                <button
                  type="button"
                  class="c-btn c-btn--narrow"
                  @click.prevent="onMainArtistAddClicked">
                  新規アーティスト追加
                  <span class="c-btn__icon-add">
                    <span></span>
                  </span>
                </button>
              </div>
            </div>
            <div class="form-group-wrap">
              <div class="form-group-wrap__title">
                Featuringアーティスト
                <span>存在する場合、1名ずつ [追加] で登録します。
                  <i
                    v-tooltip="pcTooltipConfig(tooltipMessages.featuring_artists)"
                    class="tooltip-icon">
                  </i>
                  <i
                    v-tooltip="spTooltipConfig(tooltipMessages.featuring_artists)"
                    class="sp-tooltip-icon">
                  </i>
                </span>
              </div>
              <div
                v-for="(artist, index) in item.featuring_artists"
                :key="index">
                <div v-if="artist.type !==3">
                  <div
                    :class="{ 'not-first-space': index === 0 }"
                    class="form-group form-group--set">
                    <div class="form-label">
                      <label
                        class="control-label"
                        :for="`featuring_artist_name_ja_${index}`">アーティスト名
                        <span class="req">＊</span>
                      </label>
                      <i
                        v-tooltip="spTooltipConfig(tooltipMessages.featuring_artist_name_ja)"
                        class="sp-tooltip-icon">
                      </i>
                    </div>
                    <div class="form-item">
                      <input
                        :id="`featuring_artist_name_ja_${index}`"
                        v-model="artist.name_ja"
                        v-tooltip="pcTooltipConfig(tooltipMessages.featuring_artist_name_ja)"
                        class="form-control"
                        type="text"
                        :disabled="artist.id"
                        @blur="onFieldChanged(`featuring_artists.${index}.name_ja`)">
                      <p
                        v-if="errors[`featuring_artists.${index}.name_ja`]"
                        class="error-text">
                        {{ errors[`featuring_artists.${index}.name_ja`][0] }}
                      </p>
                      <p
                        v-else-if="featArtistWarnErrors[`featuring_artists.${index}.name_ja`]"
                        class="error-text">
                        {{ featArtistWarnErrors[`featuring_artists.${index}.name_ja`] }}
                      </p>
                    </div>
                  </div>
                  <div class="form-group form-group--set">
                    <div class="form-label">
                      <label
                        class="control-label"
                        :for="`featuring_artist_name_kana_${index}`">アーティスト名（カナ表記）
                        <span class="req">＊</span>
                      </label>
                      <i
                        v-tooltip="spTooltipConfig(tooltipMessages.featuring_artist_name_kana)"
                        class="sp-tooltip-icon">
                      </i>
                    </div>
                    <div class="form-item">
                      <input
                        :id="`featuring_artist_name_kana_${index}`"
                        v-model="artist.name_kana"
                        v-tooltip="pcTooltipConfig(tooltipMessages.featuring_artist_name_kana)"
                        class="form-control"
                        type="text"
                        :disabled="artist.id"
                        @blur="onFieldChanged(`featuring_artists.${index}.name_kana`)">
                      <p
                        v-if="errors[`featuring_artists.${index}.name_kana`]"
                        class="error-text">
                        {{ errors[`featuring_artists.${index}.name_kana`][0] }}
                      </p>
                    </div>
                  </div>
                  <div class="form-group form-group--set">
                    <div class="form-label">
                      <label
                        class="control-label"
                        :for="`featuring_artist_name_en_${index}`">アーティスト名（アルファベット表記）
                        <span class="req">＊</span>
                      </label>
                      <i
                        v-tooltip="spTooltipConfig(tooltipMessages.featuring_artist_name_en)"
                        class="sp-tooltip-icon">
                      </i>
                    </div>
                    <div class="form-item">
                      <input
                        :id="`featuring_artist_name_en_${index}`"
                        v-model="artist.name_en"
                        v-tooltip="pcTooltipConfig(tooltipMessages.featuring_artist_name_en)"
                        class="form-control"
                        type="text"
                        :disabled="artist.id"
                        @blur="onFieldChanged(`featuring_artists.${index}.name_en`)">
                      <p
                        v-if="errors[`featuring_artists.${index}.name_en`]"
                        class="error-text">
                        {{ errors[`featuring_artists.${index}.name_en`][0] }}
                      </p>
                    </div>
                  </div>
                </div>
                <div
                  v-else
                  class="vocaloid-image-input">
                  <div class="vocaloid-image-container">
                    <img
                      :src="`/images/${artist.image_path}`" />
                  </div>
                  <div class="vocaloid-input">
                    <div
                      :class="{ 'not-first-space': index === 0 }"
                      class="form-group form-group--set">
                      <div class="form-label">
                        <label
                          class="control-label"
                          :for="`main_artist_name_ja_${index}`">ボーカロイド名
                          <span class="req">＊</span>
                        </label>
                        <i
                          v-tooltip="spTooltipConfig(tooltipMessages.main_artist_name_ja)"
                          class="sp-tooltip-icon">
                        </i>
                      </div>
                      <div class="form-item">
                        <input
                          :id="`main_artist_name_ja_${index}`"
                          v-model="artist.name_ja"
                          v-tooltip="pcTooltipConfig(tooltipMessages.main_artist_name_ja)"
                          class="form-control"
                          type="text"
                          :disabled="artist.id"
                          @blur="onFieldChanged(`main_artists.${index}.name_ja`)">
                        <p
                          v-if="errors[`main_artists.${index}.name_ja`]"
                          class="error-text">
                          {{ errors[`main_artists.${index}.name_ja`][0] }}
                        </p>
                      </div>
                    </div>
                    <div class="form-group form-group--set">
                      <div class="form-label">
                        <label
                          class="control-label"
                          :for="`main_artist_name_kana_${index}`">ボーカロイド名（カナ表記）
                          <span class="req">＊</span>
                        </label>
                        <i
                          v-tooltip="spTooltipConfig(tooltipMessages.main_artist_name_kana)"
                          class="sp-tooltip-icon">
                        </i>
                      </div>
                      <div class="form-item">
                        <input
                          :id="`main_artist_name_kana_${index}`"
                          v-model="artist.name_kana"
                          v-tooltip="pcTooltipConfig(tooltipMessages.main_artist_name_kana)"
                          class="form-control"
                          type="text"
                          :disabled="artist.id"
                          @blur="onFieldChanged(`main_artists.${index}.name_kana`)">
                        <p
                          v-if="errors[`main_artists.${index}.name_kana`]"
                          class="error-text">
                          {{ errors[`main_artists.${index}.name_kana`][0] }}
                        </p>
                      </div>
                    </div>
                    <div class="form-group form-group--set">
                      <div class="form-label">
                        <label
                          class="control-label"
                          :for="`main_artist_name_en_${index}`">ボーカロイド名（アルファベット表記)
                          <span class="req">＊</span>
                        </label>
                        <i
                          v-tooltip="spTooltipConfig(tooltipMessages.main_artist_name_en)"
                          class="sp-tooltip-icon">
                        </i>
                      </div>
                      <div class="form-item">
                        <input
                          :id="`main_artist_name_en_${index}`"
                          v-model="artist.name_en"
                          v-tooltip="pcTooltipConfig(tooltipMessages.main_artist_name_en)"
                          class="form-control"
                          type="text"
                          :disabled="artist.id"
                          @blur="onFieldChanged(`main_artists.${index}.name_en`)">
                        <p
                          v-if="errors[`main_artists.${index}.name_en`]"
                          class="error-text">
                          {{ errors[`main_artists.${index}.name_en`][0] }}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="form-group-wrap__btn-wrap form-group-wrap__vocalo-btn-wrap">
                  <button
                    v-if="item.featuring_artists.length - 1 === index
                      && item.featuring_artists.length < maxFeaturingArtists"
                    type="button"
                    class="c-btn c-btn--narrow"
                    @click.prevent="showFeaturingArtistPopUp()">
                    マイアーティストから追加
                    <span class="c-btn__icon-add">
                      <span></span>
                    </span>
                  </button>
                  <button
                    v-if="item.featuring_artists.length - 1 === index
                      && item.featuring_artists.length < maxFeaturingArtists"
                    type="button"
                    class="c-btn c-btn--narrow"
                    @click.prevent="showVocaloidListPopUp(true)">
                    ボーカロイド/その他音声合成ソフトを追加
                    <span class="c-btn__icon-add">
                      <span></span>
                    </span>
                  </button>
                  <button
                    v-if="item.featuring_artists.length > 0"
                    type="button"
                    class="c-btn c-btn--delete-large c-btn--narrow c-btn--right"
                    @click.prevent="artist.type === 3 ? onFeatureVocaloidDeleteClicked(index,artist)
                      :onFeaturingArtistDeleteClicked(index)">
                    削除
                  </button>
                </div>
              </div>
              <div
                v-if="item.featuring_artists.length === 0"
                class="form-group-wrap__btn-wrap form-group-wrap__vocalo-btn-wrap">
                <button
                  class="c-btn c-btn--narrow"
                  type="button"
                  @click.prevent="showFeaturingArtistPopUp()">
                  マイアーティストから追加
                  <span class="c-btn__icon-add">
                    <span></span>
                  </span>
                </button>
                <button
                  type="button"
                  class="c-btn c-btn--narrow"
                  @click.prevent="showVocaloidListPopUp(true)">
                  ボーカロイド/その他音声合成ソフトを追加
                  <span class="c-btn__icon-add">
                    <span></span>
                  </span>
                </button>
              </div>
              <div
                class="form-group-wrap__btn-wrap  form-group-wrap__vocalo-btn-wrap">
                <button
                  type="button"
                  class="c-btn c-btn--narrow"
                  @click.prevent="onFeaturingArtistAddClicked">
                  新規アーティスト追加
                  <span class="c-btn__icon-add">
                    <span></span>
                  </span>
                </button>
              </div>
            </div>
            <div class="form-group">
              <div class="form-label">
                <label class="control-label">ボーカル入り
                  <span class="req">＊</span>
                  <i
                    v-tooltip="pcTooltipConfig(tooltipMessages.vocal)"
                    class="tooltip-icon">
                  </i>
                  <i
                    v-tooltip="spTooltipConfig(tooltipMessages.vocal)"
                    class="sp-tooltip-icon">
                  </i>
                </label>
              </div>
              <div class="form-item">
                <div class="form-item-inline">
                  <div class="radio">
                    <label for="with_vocal_lyric">
                      <input
                        id="with_vocal_lyric"
                        v-model="item.vocal"
                        type="radio"
                        name="vocal"
                        :value="1"
                        checked
                        @change="onChangeWithVocal">
                      <div class="option-input"></div>
                      はい
                    </label>
                  </div>
                  <div class="radio">
                    <label for="without_vocal_music">
                      <input
                        id="without_vocal_music"
                        v-model="item.vocal"
                        type="radio"
                        name="vocal"
                        :value="0"
                        @change="onChangeWithOutVocal">
                      <div class="option-input"></div>
                      いいえ（インストゥルメンタル）
                    </label>
                  </div>
                </div>
              </div>
            </div>
            <div
              v-if="item.vocal === 1"
              class="form-group">
              <div class="form-label">
                <label class="control-label">歌詞・タイトルの表現について
                  <span class="req">＊</span>
                  <i
                    v-tooltip="pcTooltipConfig(tooltipMessages.parental_warning)"
                    class="tooltip-icon">
                  </i>
                  <i
                    v-tooltip="spTooltipConfig(tooltipMessages.parental_warning)"
                    class="sp-tooltip-icon">
                  </i>
                </label>
              </div>
              <div class="form-item">
                <div class="form-item-text">
                  Fワード、Nワードなどが、歌詞・タイトルに含まれる場合は「はい」を選択してください。<br />
                  また、ジャケット画像に [Parental Advisory] の記載がある場合、リリース内の1曲以上に“はい“を選択してください。
                </div>
                <div class="form-item-inline">
                  <span class="radio">
                    <label for="parental_warning1">
                      <input
                        id="parental_warning1"
                        v-model="item.parental_warning"
                        type="radio"
                        name="parental_warning"
                        value="explicit">
                      <div class="option-input"></div> はい
                    </label>
                  </span>
                  <span class="radio">
                    <label for="parental_warning0">
                      <input
                        id="parental_warning0"
                        v-model="item.parental_warning"
                        type="radio"
                        name="parental_warning"
                        value="no-explicit">
                      <div class="option-input"></div> いいえ
                    </label>
                  </span>
                </div>
                <p
                  v-if="errors.parental_warning"
                  class="error-text">
                  {{ errors.parental_warning[0] }}
                </p>
              </div>
            </div>
            <div
              v-if="item.vocal === 1"
              class="form-group">
              <div class="form-label">
                <label
                  class="control-label"
                  for="language">歌唱言語
                  <span class="req">＊</span>
                  <i
                    v-tooltip="pcTooltipConfig(tooltipMessages.languages)"
                    class="tooltip-icon">
                  </i>
                  <i
                    v-tooltip="spTooltipConfig(tooltipMessages.languages)"
                    class="sp-tooltip-icon">
                  </i>
                </label>
              </div>
              <div class="form-item">
                <div class="form-item-inline">
                  <div class="c-select_wrap">
                    <select
                      id="language"
                      v-model="item.language"
                      class="form-control c-select"
                      name="language">
                      <option
                        value=""
                        disabled
                        selected
                        style="display:none;">
                        選択してください
                      </option>
                      <option
                        v-for="(code, key) in languages"
                        :key="key"
                        :value="key">
                        {{ code }}
                      </option>
                    </select>
                  </div>
                  <span class="form-item-text--inline">歌詞に最も使用される言語を選択します。</span>
                </div>
              </div>
            </div>
            <div class="form-group">
              <div class="form-label">
                <label
                  class="control-label"
                  for="copyright_group">著作権管理団体
                  <span class="req">＊</span>
                  <i
                    v-tooltip="pcTooltipConfig(tooltipMessages.copyright_group)"
                    class="tooltip-icon">
                  </i>
                  <i
                    v-tooltip="spTooltipConfig(tooltipMessages.copyright_group)"
                    class="sp-tooltip-icon">
                  </i>
                </label>
              </div>
              <div class="form-item">
                <div class="form-item-inline">
                  <div class="c-select_wrap">
                    <select
                      id="copyright_group"
                      v-model="item.copyright_group"
                      class="form-control c-select"
                      name="language">
                      <option
                        value=""
                        disabled
                        selected
                        style="display:none;">
                        選択してください
                      </option>
                      <option
                        v-for="(code, key) in copyrightGroups"
                        :key="key"
                        :value="key">
                        {{ code }}
                      </option>
                    </select>
                  </div>
                  <p
                    v-if="errors.copyright_group"
                    class="error-text">
                    {{ errors.copyright_group[0] }}
                  </p>
                </div>
              </div>
            </div>
            <div class="form-group-wrap">
              <div class="form-group-wrap__title">
                著作者公表名
                <span>複数存在する場合、1名ずつ登録します。
                  <i
                    v-tooltip="pcTooltipConfig(tooltipMessages.copyright_artists)"
                    class="tooltip-icon">
                  </i>
                  <i
                    v-tooltip="spTooltipConfig(tooltipMessages.copyright_artists)"
                    class="sp-tooltip-icon">
                  </i>
                  <p class="considerations-text c-color-caution">
                    ※作曲は必ず１つは入力してください。
                  </p>
                  <p
                    v-if="item.vocal === 1"
                    class="considerations-text c-color-caution">
                    ※ボーカル入り「はい」を選択した場合は作詞を必ず１つ入力してください。
                  </p>
                </span>
                <div v-if="errors.copyright_artists && errors.copyright_artists.length > 0">
                  <p
                    v-for="error in errors.copyright_artists"
                    :key="error"
                    class="considerations-text c-color-caution">
                    {{ error }}
                  </p>
                </div>
              </div>
              <div
                v-for="(artist, index) in item.copyright_artists"
                :key="index">
                <div class="form-group form-group--set">
                  <div class="form-label">
                    <label class="control-label">識別
                    </label>
                  </div>
                  <div class="form-item-inline">
                    <div class="checkbox">
                      <label :for="`copyright_type_composer_${index}`">
                        <input
                          :id="`copyright_type_composer_${index}`"
                          v-model="artist.roles"
                          value="composer"
                          type="checkbox">
                        <div
                          class="option-input">
                        </div>
                        作曲
                      </label>
                    </div>
                    <div
                      v-if="item.vocal === 1"
                      class="checkbox">
                      <label :for="`copyright_type_lyricist_${index}`">
                        <input
                          :id="`copyright_type_lyricist_${index}`"
                          v-model="artist.roles"
                          value="lyricist"
                          type="checkbox">
                        <div class="option-input"></div>
                        作詞
                      </label>
                    </div>
                    <div class="checkbox">
                      <label :for="`copyright_type_arranger_${index}`">
                        <input
                          :id="`copyright_type_arranger_${index}`"
                          v-model="artist.roles"
                          value="arranger"
                          type="checkbox">
                        <div class="option-input"></div>
                        編曲
                      </label>
                    </div>
                    <p
                      v-if="errors[`copyright_artists.${index}.roles`]"
                      class="error-text">
                      {{ errors[`copyright_artists.${index}.roles`][0] }}
                    </p>
                  </div>
                </div>
                <div class="form-group form-group--set">
                  <div class="form-label">
                    <label
                      class="control-label"
                      :for="`copyright_artist_name_ja_${index}`">著作者公表名
                    </label>
                  </div>
                  <div class="form-item">
                    <input
                      :id="`copyright_artist_name_ja_${index}`"
                      v-model="artist.name_ja"
                      class="form-control"
                      type="text"
                      :disabled="artist.id"
                      @blur="onFieldChanged(`copyright_artists.${index}.name_ja`)">
                    <p
                      v-if="errors[`copyright_artists.${index}.name_ja`]"
                      class="error-text">
                      {{ errors[`copyright_artists.${index}.name_ja`][0] }}
                    </p>
                  </div>
                </div>
                <div class="form-group form-group--set">
                  <div class="form-label">
                    <label
                      class="control-label"
                      :for="`copyright_artist_name_en_${index}`">著作者公表名 （カナ）
                    </label>
                  </div>
                  <div class="form-item">
                    <input
                      :id="`copyright_artist_name_en_${index}`"
                      v-model="artist.name_kana"
                      class="form-control"
                      type="text"
                      :disabled="artist.id"
                      @blur="onFieldChanged(`copyright_artists.${index}.name_kana`)">
                    <p
                      v-if="errors[`copyright_artists.${index}.name_kana`]"
                      class="error-text">
                      {{ errors[`copyright_artists.${index}.name_kana`][0] }}
                    </p>
                  </div>
                </div>
                <div class="form-group form-group--set">
                  <div class="form-label">
                    <label
                      class="control-label"
                      :for="`copyright_artist_name_en_${index}`">著作者公表名 （アルファベット表記）
                    </label>
                  </div>
                  <div class="form-item">
                    <input
                      :id="`copyright_artist_name_en_${index}`"
                      v-model="artist.name_en"
                      class="form-control"
                      type="text"
                      :disabled="artist.id"
                      @blur="onFieldChanged(`copyright_artists.${index}.name_en`)">
                    <p
                      v-if="errors[`copyright_artists.${index}.name_en`]"
                      class="error-text">
                      {{ errors[`copyright_artists.${index}.name_en`][0] }}
                    </p>
                  </div>
                </div>
                <div class="form-group-wrap__btn-wrap">
                  <button
                    v-if="item.copyright_artists.length - 1 === index"
                    type="button"
                    class="c-btn c-btn--narrow sp-button"
                    @click.prevent="showCopyrightArtistPopUp">
                    マイアーティストから著作者公表名を追加
                    <span class="c-btn__icon-add">
                      <span></span>
                    </span>
                  </button>
                  <button
                    v-if="item.copyright_artists.length - 1 === index"
                    type="button"
                    class="c-btn c-btn--narrow"
                    @click.prevent="onCopyrightArtistAddClicked">
                    著作者公表名を新規追加
                    <span class="c-btn__icon-add">
                      <span></span>
                    </span>
                  </button>
                  <button
                    type="button"
                    class="c-btn c-btn--delete-large c-btn--narrow c-btn--right"
                    @click.prevent="onCopyrightArtistDeleteClicked(index)">
                    削除
                  </button>
                </div>
              </div>
              <div
                v-if="item.copyright_artists.length === 0"
                class="form-group-wrap__btn-wrap">
                <button
                  class="c-btn c-btn--narrow sp-button"
                  type="button"
                  @click.prevent="showCopyrightArtistPopUp()">
                  マイアーティストから著作者公表名を追加
                  <span class="c-btn__icon-add">
                    <span></span>
                  </span>
                </button>
                <button
                  type="button"
                  class="c-btn c-btn--narrow"
                  @click.prevent="onCopyrightArtistAddClicked">
                  著作者公表名を新規追加
                  <span class="c-btn__icon-add">
                    <span></span>
                  </span>
                </button>
              </div>
            </div>
            <div
              v-if="isBtoC"
              class="form-group-wrap">
              <div class="form-group-wrap__title">
                役割設定
                <span>各アーティストの担当を入力します。</span>
              </div>
              <p
                v-if="roleAssignmentPerformerGlobalErrors.length"
                class="error-text">
                {{ roleAssignmentPerformerGlobalErrors[0] }}
              </p>

              <div
                class="form-label"
                style="align-self: flex-start;padding-top:4px">
                <label class="control-label">
                  パフォーマンスの役割
                  <span class="req">＊</span>
                </label>
              </div>
              <div class="form-item-text form-item-text--collapsible">
                <div
                  class="role-catalog-preview"
                  :class="{ 'role-catalog-preview--expanded': isPerformanceRoleCatalogExpanded }">
                  <span
                    v-for="(catalog, index) in performanceRoleCatalog"
                    :key="`performance-role-${catalog.key}`">
                    {{ catalog.labels.ja }}
                    <span v-if="performanceRoleCatalog.length > index + 1">,</span>
                  </span>
                </div>
                <button
                  v-if="performanceRoleCatalog.length > 0"
                  type="button"
                  class="role-catalog-toggle"
                  @click="isPerformanceRoleCatalogExpanded = !isPerformanceRoleCatalogExpanded">
                  {{ isPerformanceRoleCatalogExpanded ? '閉じる-' : 'すべて表示+' }}
                </button>
              </div>
              <role-assignment-row
                v-for="(assignment, index) in performanceRoleAssignments"
                :key="`performer-${assignment.key}`"
                :assignment="assignment"
                :index="index"
                :performance-catalog="roleCatalog.performance"
                :production-catalog="roleCatalog.production"
                :errors="roleAssignmentErrors[assignment.key] || {}"
                :is-last="index === performanceRoleAssignments.length - 1"
                @update="onRoleAssignmentUpdated(assignment.key, $event)"
                @show-artist-modal="showPerformanceRoleAssignmentModal"
                @show-performance-role-modal="showPerformanceRoleModal"
                @add-new-artist="onPerformanceRoleAssignmentNewArtistAdd"
                @delete="deleteRoleAssignment(assignment.key, 'performance')" />
              <div
                v-if="!performanceRoleAssignments.length"
                class="form-group-wrap__btn-wrap">
                <button
                  type="button"
                  class="c-btn c-btn--narrow"
                  @click="showPerformanceRoleAssignmentModal">
                  マイアーティストから追加
                  <span class="c-btn__icon-add">
                    <span></span>
                  </span>
                </button>
                <button
                  type="button"
                  class="c-btn c-btn--narrow"
                  @click="onPerformanceRoleAssignmentNewArtistAdd">
                  新規アーティスト追加
                  <span class="c-btn__icon-add">
                    <span></span>
                  </span>
                </button>
              </div>
              <div style="margin-top: 20px; margin-left: 20px; border-bottom: 1px dashed #c0c0c0" />
              <p
                v-if="roleAssignmentProductionGlobalErrors.length"
                class="error-text">
                {{ roleAssignmentProductionGlobalErrors[0] }}
              </p>

              <div
                class="form-label"
                style="align-self: flex-start;padding-top:4px">
                <div class="form-label">
                  <label class="control-label">
                    プロダクション＆エンジニアリングの役割
                    <span class="req">＊</span>
                  </label>
                </div>
              </div>

              <div class="form-item-text form-item-text--collapsible">
                <div
                  class="role-catalog-preview"
                  :class="{ 'role-catalog-preview--expanded': isProductionRoleCatalogExpanded }">
                  <span
                    v-for="(catalog, index) in productionRoleCatalog"
                    :key="`product-role-${catalog.key}`">
                    {{ catalog.labels.ja }}
                    <span v-if="productionRoleCatalog.length > index + 1">,</span>
                  </span>
                </div>
                <button
                  v-if="productionRoleCatalog.length > 0"
                  type="button"
                  class="role-catalog-toggle"
                  @click="isProductionRoleCatalogExpanded = !isProductionRoleCatalogExpanded">
                  {{ isProductionRoleCatalogExpanded ? '閉じる-' : 'すべて表示+' }}
                </button>
              </div>
              <role-assignment-row
                v-for="(assignment, index) in productionRoleAssignments"
                :key="`production-${assignment.key}`"
                :assignment="assignment"
                :index="index"
                :performance-catalog="roleCatalog.performance"
                :production-catalog="roleCatalog.production"
                :errors="roleAssignmentErrors[assignment.key] || {}"
                :is-last="index === productionRoleAssignments.length - 1"
                @update="onRoleAssignmentUpdated(assignment.key, $event)"
                @show-artist-modal="showProductionRoleAssignmentModal"
                @show-production-role-modal="showProductionRoleModal"
                @add-new-artist="onProductionRoleAssignmentNewArtistAdd"
                @delete="deleteRoleAssignment(assignment.key, 'production')" />
              <div
                v-if="!productionRoleAssignments.length"
                class="form-group-wrap__btn-wrap">
                <button
                  type="button"
                  class="c-btn c-btn--narrow"
                  @click="showProductionRoleAssignmentModal">
                  マイアーティストから追加
                  <span class="c-btn__icon-add">
                    <span></span>
                  </span>
                </button>
                <button
                  type="button"
                  class="c-btn c-btn--narrow"
                  @click="onProductionRoleAssignmentNewArtistAdd">
                  新規アーティスト追加
                  <span class="c-btn__icon-add">
                    <span></span>
                  </span>
                </button>
              </div>
            </div>
            <div
              v-if="isBtoB && type === 'album'"
              class="form-group">
              <div class="form-label">
                <label
                  class="control-label"
                  for="copyright_p">著作権表記(C)
                </label>
              </div>
              <div class="form-item">
                <input
                  id="copyright_c"
                  v-model="item.copyright_c_copyright"
                  class="form-control copyright-form-control"
                  type="text"
                  placeholder="XXXXX Records"
                  @blur="onFieldChanged('copyright_c_copyright')">
                <p
                  v-if="errors.copyright_c_copyright"
                  class="error-text">
                  {{ errors.copyright_c_copyright[0] }}
                </p>
              </div>
              <div class="form-item-inline form-item-inline--year">
                <input
                  id="copyright_c_year"
                  v-model="item.copyright_c_year"
                  class="form-control copyright-year-form-control"
                  type="text"
                  maxlength="4"
                  placeholder="2020"
                  @blur="onFieldChanged('copyright_c_year')">
                <p
                  v-if="errors.copyright_c_year"
                  class="error-text">
                  {{ errors.copyright_c_year[0] }}
                </p>
              </div>
            </div>

            <div
              v-if="isBtoB && type === 'album'"
              class="form-group">
              <div class="form-label">
                <label
                  class="control-label"
                  for="copyright_p">原盤権表記(P)
                </label>
              </div>
              <div class="form-item">
                <input
                  id="copyright_p"
                  v-model="item.copyright_p_copyright"
                  class="form-control copyright-form-control"
                  type="text"
                  placeholder="XXXXX Records"
                  @blur="onFieldChanged('copyright_p_copyright')">
                <p
                  v-if="errors.copyright_p_copyright"
                  class="error-text">
                  {{ errors.copyright_p_copyright[0] }}
                </p>
              </div>
              <div class="form-item-inline form-item-inline--year">
                <input
                  id="copyright_p_year"
                  v-model="item.copyright_p_year"
                  class="form-control copyright-year-form-control"
                  type="text"
                  maxlength="4"
                  placeholder="2020"
                  @blur="onFieldChanged('copyright_p_year')">
                <p
                  v-if="errors.copyright_p_year"
                  class="error-text">
                  {{ errors.copyright_p_year[0] }}
                </p>
              </div>
            </div>
            <div class="form-group">
              <div class="form-label">
                <label
                  class="control-label"
                  for="isrc">ISRC
                  <i
                    v-tooltip="pcTooltipConfig(
                      isBtoC ? tooltipMessages.isrc : tooltipMessages.isrc_tob,
                      true)"
                    class="tooltip-icon">
                  </i>
                  <i
                    v-tooltip="spTooltipConfig(tooltipMessages.isrc)"
                    class="sp-tooltip-icon">
                  </i>
                </label>
              </div>
              <div class="form-item">
                <input
                  id="isrc"
                  v-model="item.isrc"
                  class="form-control"
                  type="text"
                  @change="handleIsrcChange">
                <p class="form-item-text">
                  ISRCコードは、それぞれの楽曲（録音）を識別するための固有の番号です。<br>
                  そのため、一度発行したISRCコードは他の曲には使わないようにご注意ください。
                </p>
                <p
                  v-if="errors.isrc"
                  class="error-text">
                  {{ errors.isrc[0] }}
                </p>
                <confirm-dialog
                  name="pndrIsrcWarning"
                  :is-shown="isShownPndrIsrcWarning"
                  :cancel-button-title="false"
                  title="警告"
                  @closed="isShownPndrIsrcWarning = false"
                  @default-clicked="isShownPndrIsrcWarning = false"
                  @cancel-clicked="isShownPndrIsrcWarning = false">
                  <template #body>
                    {{ pndrIsrcWarning }} <br>
                    よろしいでしょうか？
                  </template>
                </confirm-dialog>
              </div>
            </div>
            <div class="form-group">
              <div class="form-label">
                <label
                  class="control-label"
                  for="listening_start_position">試聴開始位置
                  <i
                    v-tooltip="pcTooltipConfig(tooltipMessages.listening_position)"
                    class="tooltip-icon">
                  </i>
                  <i
                    v-tooltip="spTooltipConfig(tooltipMessages.listening_position)"
                    class="sp-tooltip-icon">
                  </i>
                </label>
              </div>
              <div class="form-item-inline">
                <div class="form-item">
                  <div class="form-item-inline form-item-inline--sp-reset">
                    <span class="form-item__wrap">
                      <input
                        id="listening_start_position"
                        v-model="item.trial_start_min"
                        class="form-control form-control--time"
                        type="text"
                        maxlength="2">分
                    </span>
                    <span class="form-item__wrap">
                      <input
                        id="listening_start_position_second"
                        v-model="item.trial_start_sec"
                        class="form-control form-control--time"
                        type="text"
                        maxlength="2">秒
                    </span>
                  </div>
                  <p
                    v-if="errors.trial_start_min"
                    class="error-text">
                    {{ errors.trial_start_min[0] }}
                  </p>
                  <p
                    v-if="errors.trial_start_sec"
                    class="error-text">
                    {{ errors.trial_start_sec[0] }}
                  </p>
                </div>
              </div>
            </div>

            <div
              v-if="isBtoC"
              class="form-group">
              <div class="form-label">
                <label class="control-label">生成AIの利用について
                  <span class="req">＊</span>
                  <i
                    v-tooltip="pcTooltipConfig(tooltipMessages.generativeAi)"
                    class="tooltip-icon">
                  </i>
                  <i
                    v-tooltip="spTooltipConfig(tooltipMessages.generativeAi)"
                    class="sp-tooltip-icon">
                  </i>
                </label>
              </div>
              <div class="form-item">
                <div class="form-item-inline">
                  <div class="radio">
                    <label for="is_ai_use">
                      <input
                        id="is_ai_use"
                        v-model="item.is_ai_use"
                        type="radio"
                        name="ai_use"
                        :value="1">
                      <div class="option-input"></div>
                      使用している
                    </label>
                  </div>
                  <div class="radio">
                    <label for="not_ai_use">
                      <input
                        id="not_ai_use"
                        v-model="item.is_ai_use"
                        type="radio"
                        name="ai_use"
                        :value="0">
                      <div class="option-input"></div>
                      使用していない
                    </label>
                  </div>
                </div>
                <div
                  v-if="item.is_ai_use"
                  class="form-item-text">
                  「使用している」を選択した場合、権利物の種類に「生成AIによって制作された音源」を追加する必要があります
                </div>
                <p
                  v-if="errors.is_ai_use"
                  class="error-text">
                  {{ errors.is_ai_use[0] }}
                </p>
              </div>
            </div>
            <rights-documents
              v-if="isBtoC"
              :key="rightsDocumentsKey"
              v-model="rightsDocuments"
              :use-rights="item.use_rights"
              :errors="errors"
              @update-rights-check="checkUseRights" />
            <div
              v-if="item.classification===1"
              class="form-group">
              <div class="form-label">
                <label class="control-label">フリートラックの使用について
                  <span class="req">＊</span>
                  <i
                    v-tooltip="pcTooltipConfig(tooltipMessages.freetrack)"
                    class="tooltip-icon">
                  </i>
                  <i
                    v-tooltip="spTooltipConfig(tooltipMessages.freetrack)"
                    class="sp-tooltip-icon">
                  </i>
                </label>
              </div>
              <div class="form-item">
                <div class="form-item-inline">
                  <div class="radio">
                    <label for="use_freetrack">
                      <input
                        id="use_freetrack"
                        v-model="item.freetrack"
                        type="radio"
                        name="freetrack"
                        :value="1">
                      <div class="option-input"></div>
                      使用している
                    </label>
                  </div>
                  <div class="radio">
                    <label for="not_use_freetrack">
                      <input
                        id="not_use_freetrack"
                        v-model="item.freetrack"
                        type="radio"
                        name="freetrack"
                        :value="0">
                      <div class="option-input"></div>
                      使用していない
                    </label>
                  </div>
                </div>
                <div class="form-item-text">
                  <router-link
                    v-if="false"
                    :to="{ name : 'faq-detail', params: {id: isBtoC ? 143 : 0} }"
                    class="c-text-link"
                    target="_blank">
                    フリートラックとは
                  </router-link>
                </div>
                <p
                  v-if="errors.freetrack"
                  class="error-text">
                  {{ errors.freetrack[0] }}
                </p>
              </div>
            </div>
            <div
              v-if="item.classification===1 && isBtoC"
              class="form-group">
              <div class="form-label">
                <label class="control-label">著作権フリー楽曲の使用について
                  <span class="req">＊</span>
                  <i
                    v-tooltip="pcTooltipConfig(tooltipMessages.use_copyright_free)"
                    class="tooltip-icon">
                  </i>
                  <i
                    v-tooltip="spTooltipConfig(tooltipMessages.use_copyright_free)"
                    class="sp-tooltip-icon">
                  </i>
                </label>
              </div>
              <div class="form-item">
                <div class="form-item-inline">
                  <div class="radio">
                    <label for="use_copyright_free">
                      <input
                        id="use_copyright_free"
                        v-model="item.useCopyrightFreek"
                        type="radio"
                        name="useCopyrightFreek"
                        :value="1">
                      <div class="option-input"></div>
                      使用している
                    </label>
                  </div>
                  <div class="radio">
                    <label for="not_use_copyright_free">
                      <input
                        id="not_use_copyright_free"
                        v-model="item.useCopyrightFreek"
                        type="radio"
                        name="useCopyrightFreek"
                        :value="0">
                      <div class="option-input"></div>
                      使用していない
                    </label>
                  </div>
                </div>
                <div class="form-item-text">
                  <router-link
                    v-if="false"
                    :to="{ name : 'faq-detail', params: {id: isBtoC ? 144 : 0} }"
                    class="c-text-link"
                    target="_blank">
                    著作権フリーとは
                  </router-link>
                </div>
                <p
                  v-if="errors.useCopyrightFreek"
                  class="error-text">
                  {{ errors.useCopyrightFreek[0] }}
                </p>
              </div>
            </div>
            <div
              v-if="item.classification===1 && isBtoC"
              class="form-group">
              <div class="form-label">
                <label
                  class="control-label"
                  style="white-space: normal;padding-right:20px;">著作権フリー楽曲として販売していた実績がある
                  <span class="req">＊</span>
                  <i
                    v-tooltip="pcTooltipConfig(tooltipMessages.has_sold_copyright_free)"
                    class="tooltip-icon">
                  </i>
                  <i
                    v-tooltip="spTooltipConfig(tooltipMessages.has_sold_copyright_free)"
                    class="sp-tooltip-icon">
                  </i>
                </label>
              </div>
              <div class="form-item">
                <div class="form-item-inline">
                  <div class="radio">
                    <label for="has_sold_copyright_free">
                      <input
                        id="has_sold_copyright_free"
                        v-model="item.hasSoldCopyrightFree"
                        type="radio"
                        name="hasSoldCopyrightFree"
                        :value="1">
                      <div class="option-input"></div>
                      はい
                    </label>
                  </div>
                  <div class="radio">
                    <label for="has_not_sold_copyright_free">
                      <input
                        id="has_not_sold_copyright_free"
                        v-model="item.hasSoldCopyrightFree"
                        type="radio"
                        name="hasSoldCopyrightFree"
                        :value="0">
                      <div class="option-input"></div>
                      いいえ
                    </label>
                  </div>
                </div>
                <p
                  v-if="errors.hasSoldCopyrightFree"
                  class="error-text">
                  {{ errors.hasSoldCopyrightFree[0] }}
                </p>
              </div>
            </div>

            <div
              v-if="!disableContentIdUser && item.classification===1"
              class="form-group"
              style="border: none;"
              :class="{ disabled: isSpokenWord || disableContentId}">
              <div class="form-label">
                <label class="control-label">YouTubeマネタイズサービス
                  <span class="req">＊</span>
                  <i
                    v-tooltip="'YouTubeマネタイズサービスを利用すると自動でYoutube Musicにも配信されます。'"
                    class="tooltip-icon">
                  </i>
                  <i
                    v-tooltip="spTooltipConfig('YouTubeマネタイズサービスを利用すると自動でYoutube Musicにも配信されます。')"
                    class="sp-tooltip-icon">
                  </i>
                </label>
              </div>
              <div class="form-item">
                <div class="form-item-inline">
                  <div class="radio">
                    <label for="with_content_id">
                      <input
                        id="with_content_id"
                        v-model="enableContentId"
                        type="radio"
                        name="contentsID"
                        class="form-control"
                        :disabled="isSpokenWord || disableContentId"
                        :value="1"
                        @change="onFieldChanged('_restrictions_0_invalid_youtube_monetize')">
                      <div class="option-input"></div>
                      利用する
                    </label>
                  </div>
                  <div class="radio">
                    <label for="without_content_id">
                      <input
                        id="without_content_id"
                        v-model="enableContentId"
                        type="radio"
                        name="contentsID"
                        class="form-control"
                        :disabled="isSpokenWord || disableContentId"
                        :value="0"
                        @change="onFieldChanged('_restrictions_0_invalid_youtube_monetize')">
                      <div class="option-input"></div>
                      利用しない
                    </label>
                  </div>
                  <div
                    v-if="false"
                    class="radio">
                    <label for="both_content_id">
                      <input
                        id="both_content_id"
                        v-model="enableContentId"
                        type="radio"
                        name="contentsID"
                        :value="2">
                      <div class="option-input"></div>
                      配信 + 利用
                    </label>
                  </div>
                </div>
                <div v-if="isSpokenWord">
                  <span style="font-size: 14px; color: red;">
                    ジャンルに「Spoken Word」が設定されているので、YouTubeマネタイズサービスは利用出来ません。
                  </span>
                </div>
                <p
                  v-if="errors.enableContentId"
                  class="error-text">
                  {{ errors.enableContentId[0] }}
                </p>
                <div
                  v-if="isBtoB"
                  class="form-item-text">
                  <router-link
                    :to="{ name : 'faq-detail', params: {id: isBtoC ? 58 : 33} }"
                    class="c-text-link"
                    target="_blank">
                    YouTubeマネタイズサービスとは？
                  </router-link>
                </div>
              </div>
            </div>
            <div
              v-if="isBtoC && !disableContentIdUser && item.classification===1"
              style="margin-top: -20px"
              class="form-group"
              :class="{ disabled: isSpokenWord || disableContentId}">
              <div class="form-label"></div>
              <div class="form-item">
                <p
                  style="margin-left: 0!important;"
                  class="considerations-text c-color-caution">
                  YouTubeマネタイズサービスの内容と注意事項をお読みいただき下記の同意をお願いします。<br />
                  ※最後までスクロールしていただかないと、同意のチェックができません。
                </p>
                <div
                  style="text-align: center"
                  :class="{ 'apple': isAppleDevice }">
                  <div
                    class="agree-area"
                    @scroll="onScrollAgreement">
                    YouTubeマネタイズサービス
                    <span class="asterisk">*1</span>
                    とは、第三者がYouTubeにアップロードする動画にあなたの楽曲が使用されると、
                    音源の権利として収益を受け取ることができるサービスです。ぜひご活用ください。
                    <br />
                    <a
                      href="https://support.google.com/youtube/answer/2797370?hl=ja"
                      class="c-text-link"
                      target="_blank">
                      詳しくはこちら。
                    </a>
                    <br /><br /><br />
                    ただし、当サービスから配信している楽曲を使用した動画をYouTubeへアップする場合、
                    YouTube上で「著作権侵害の申し立て」と判断される可能性があります。<br />
                    その際、著作権者として 「massenext.Inc(narasuの代理会社)」
                    <span class="asterisk">*2</span>
                    と記載されます。<br />
                    ただしこの場合の「著作権」とは「原盤権（音源に関する権利）」のことを指しており、
                    「作曲や作詞に関する著作権」のことではありませんのでご注意ください。<br />
                    著作権侵害の申し立ての取り下げをご希望の方は、
                    <a
                      href="/client/contact/"
                      class="c-text-link"
                      target="_blank">
                      お問い合わせフォーム
                    </a>
                    より下記を明記のうえお問い合わせください。<br /><br />
                    ●著作権侵害の申し立てに関して<br />
                    &nbsp;・申し立てを受けた動画URL:<br />
                    &nbsp;・対象楽曲名:<br />
                    &nbsp;・対象リリースのJANコード:<br />
                    <br /><br />
                    「YouTubeマネタイズサービス」の登録対象外の楽曲は以下となります。<br />
                    <ul>
                      <li class="list">パプリックドメインに帰した知的創作物を含む楽曲（クラシック楽曲の音源など）</li>
                      <li class="list">第三者が権利を有する、またはライセンスしている音源を含む楽曲（サンプリング音源・ライセンス音源など）</li>
                      <li class="list">フリー素材の音源を含む楽曲（音楽制作ソフトのループ音源素材・サウンドエフェクト音源素材など）</li>
                      <li class="list">オリジナル楽曲と差異のないカバー楽曲（有名アーティストの歌真似など）</li>
                      <li class="list">オリジナル楽曲のカラオケ、アカペラ、インスト、ショート、リマスター、ライブバージョンなどの楽曲</li>
                      <li class="list">オリジナル盤以外のアルバム（コンピレーション盤・リマスター盤・ベスト盤・DJミックス盤など）</li>
                      <li class="list">30秒未満の楽曲</li>
                      <li class="list">音楽以外のポッドキャスト、スピーチ、インタビュー、朗読などのコンテンツ</li>
                      <li class="list">音の識別が難しい楽曲(自然環境音、アンビエント、ヨガ、スリープに部類される楽曲など)</li>
                      <li class="list">当社以外のサービスから YouTube『YouTubeマネタイズサービス』へ登録された楽曲</li>
                    </ul>
                    ※『YouTubeマネタイズサービス』へのご登録後、当社以外からのContent ID登録が確認できた場合、ご連絡を差し上げずに登録を解除する場合があります。
                    <br />
                    <br />
                    <span class="asterisk">*1</span><br />
                    「YouTubeマネタイズサービス」を利用すると、自動でYouTube Musicにも配信されます<br /><br />
                    <span class="asterisk">*2</span><br />
                    narasuでは、YouTube(google)と音楽契約をもつ株式会社massenextと業務提携しており、
                    「YouTubeマネタイズサービス」に関しては、音楽エージェントサイトZULAを運営する株式会社massenextを通じて運用管理をしております。<br />
                    YouTube上では「著作権」という表記の中に「原盤権」も含まれており、ここでの「著作権」とは「原盤権」のことを指します。<br />
                    <br />
                  </div>
                </div>
                <div class="consent-checkbox-area">
                  <div class="checkbox">
                    <label>
                      <input
                        v-model="item.youtube_monetize_agreed"
                        :value="1"
                        :disabled="!readAgreement || alreadyAgreed || disableContentId"
                        type="checkbox">
                      <div
                        class="option-input"
                        :style="{ 'opacity': readAgreement ? '100%' : '30%' }">
                      </div>
                      YouTubeマネタイズサービスの内容と注意事項を理解・同意しました
                    </label>
                  </div>
                </div>
                <p
                  v-if="errors._restrictions_0_invalid_youtube_monetize"
                  class="error-text">
                  {{ errors._restrictions_0_invalid_youtube_monetize[0] }}
                </p>
              </div>
            </div>
            <div
              v-if="false"
              class="form-group">
              <div class="form-label form-label--top">
                <label
                  class="control-label"
                  for="song_introduction_domestic">楽曲紹介文
                </label>
                <i
                  v-tooltip="spTooltipConfig(tooltipMessages.remarks_ja)"
                  class="sp-tooltip-icon">
                </i>
              </div>
              <div class="form-item">
                <textarea
                  id="song_introduction_domestic"
                  v-model="item.remarks_ja"
                  v-tooltip="pcTooltipConfig(tooltipMessages.remarks_ja)"
                  class="c-textarea">
                </textarea>
                <p
                  v-if="errors.remarks_ja"
                  class="error-text">
                  {{ errors.remarks_ja[0] }}
                </p>
              </div>
            </div>
            <div
              v-if="false"
              class="form-group">
              <div class="form-label form-label--top">
                <label
                  class="control-label"
                  for="song_introduction_overseas">
                  楽曲紹介文（アルファベット表記）
                </label>
                <i
                  v-tooltip="spTooltipConfig(tooltipMessages.remarks_en)"
                  class="sp-tooltip-icon">
                </i>
              </div>
              <div class="form-item">
                <textarea
                  id="song_introduction_overseas"
                  v-model="item.remarks_en"
                  v-tooltip="pcTooltipConfig(tooltipMessages.remarks_en)"
                  class="c-textarea">
                </textarea>
                <p
                  v-if="errors.remarks_en"
                  class="error-text">
                  {{ errors.remarks_en[0] }}
                </p>
              </div>
            </div>
            <VocaloidContest
              :bus="bus"
              :track="item"
              :product-id="productId" />
          </div>
        </form>
        <alert-view
          v-if="!realtimeValidation"
          :errors="errors" />
        <div class="c-btn-wrap">
          <a
            v-if="isBtoC
              && !disableContentId
              && (item.contents_id_enabled === ''
                || (item.contents_id_enabled === 1 && !item.youtube_monetize_agreed)
              )"
            class="c-btn c-btn--back">
            登録する
            <span class="c-btn__icon-arrow"></span>
          </a>
          <a
            v-else
            class="c-btn"
            href="#"
            @click.prevent="onRegisterClicked(null, 0)">
            登録する
            <span class="c-btn__icon-arrow"></span>
          </a>
          <a
            class="c-btn c-btn--back c-btn--back-left"
            href="#"
            @click.prevent="onReturnClicked">
            戻る
            <span class="c-btn__icon-arrow c-btn__icon-arrow--left"></span>
          </a>
        </div>
      </div>
    </div>
    <basic-modal
      v-if="popUpMyArtistDialogShown"
      :is-active="popUpMyArtistDialogShown"
      :remaining-number="100"
      :already-selected-vocaloid="selectedArtists"
      :comparable-vocaloid="[]"
      :is-adding-for-featuring="false"
      :is-selected-user-artist="isSelectedUserArtist"
      width="700px"
      title="マイアーティスト"
      @item-to-be-deleted="itemToBeDeleted($event)"
      @modalConfirm="modalConfirm($event)"
      @modal-cancel="closeModel">
    </basic-modal>
    <basic-modal
      v-if="popUpDialogShown"
      :is-active="popUpDialogShown"
      :remaining-number="isFeaturing ?
        100-(item.main_artists.length + selectedFeatureVocaloids.length)
        : ((artistType === 2)? 100: 4)-(item.main_artists.length)"
      :already-selected-vocaloid="isFeaturing ? selectedFeatureVocaloids : selectedVocaloids"
      :comparable-vocaloid="isFeaturing ? selectedVocaloids : selectedFeatureVocaloids "
      :is-adding-for-featuring="isFeaturing"
      :is-selected-user-artist="isSelectedUserArtist"
      width="700px"
      title="ボーカロイド"
      @item-to-be-deleted="itemToBeDeleted($event)"
      @modalConfirm="modalConfirm($event)"
      @modal-cancel="closeModel">
    </basic-modal>
  </div>
</template>

<script>
import moment from 'moment'
import Vue from 'vue'
import ReleaseStepArea from '../StepArea'
import HeaderBar from '../../HeaderBar'
import ReleaseTypeLabel from '../ReleaseTypeLabel'
import AlertView from '../../AlertView'
import tooltip from '../../../mixins/tooltip'
import tooltipMessages from './track-tooltips'
import BasicModal from '../../../../components/Modal'
import VocaloidContest from '../../Campaign/VocaloidContest'
import RightsDocuments from './RightsDocuments'
import ConfirmDialog from '../../../../components/ConfirmDialog'
import SelectMainArtist from './SelectMainArtist'
import SelectFeaturingArtist from './SelectFeaturingArtist'
import SelectCopyrightArtist from './SelectCopyrightArtist'
import SelectRoleAssignmentArtist from './SelectRoleAssignmentArtist'
import RoleAssignmentRow from './RoleAssignmentRow'
import RoleSelectionDialog from './RoleSelectionDialog'

let vTimer = null
export default {
  name: 'ReleaseStep2Track',
  components: {
    ReleaseTypeLabel,
    HeaderBar,
    ReleaseStepArea,
    AlertView,
    BasicModal,
    VocaloidContest,
    RightsDocuments,
    ConfirmDialog,
    SelectMainArtist,
    SelectFeaturingArtist,
    SelectCopyrightArtist,
    SelectRoleAssignmentArtist,
    RoleAssignmentRow,
    RoleSelectionDialog,
  },
  mixins: [
    tooltip,
  ],
  props: {
    tooltipMessages: {
      type: Object,
      default: () => tooltipMessages,
    },
    maxFeaturingArtists: {
      type: Number,
      default: 50,
    },
  },
  data() {
    return {
      type: '',
      artistType: 2,
      artists: [],
      discIndex: null,
      trackIndex: null,
      item: {
        title_ja: '',
        title_kana: '',
        title_en: '',
        version_ja: '',
        version_kana: '',
        version_en: '',
        main_artists: [],
        featuring_artists: [],
        vocal: 1,
        freetrack: null,
        useCopyrightFreek: null,
        hasSoldCopyrightFree: null,
        parental_warning: 'no-explicit',
        language: 'ja',
        lyrics: '',
        copyright_group: '7',
        copyright_artists: [],
        id: 0,
        isrc: '',
        trial_start_min: 0,
        trial_start_sec: 0,
        remarks_ja: '',
        remarks_en: '',
        is_omnibus: 0,
        is_ai_use: 0,
        use_rights: 0,
        copyright_p_copyright: '',
        copyright_p_year: '',
        copyright_c_copyright: '',
        copyright_c_year: '',
        classification: 1,
        permission_cover_music: null,
        contents_id_enabled: '',
        duration: null,
        youtube_monetize_agreed: false,
        _restrictions: [],
        vocaloid: [],
        role_assignments: [],
        role_prefill_mode: 'manual',
      },
      errors: {},
      languages: [],
      copyrightGroups: [],
      version_status: false,
      realtimeValidation: true,
      enableContentId: '',
      hideVersion: false,
      hasInitialDigit: false,
      versionAddedCover: false,
      releaseArtistsCount: 1,
      showYouTubeMonetizeDialog: false,
      readAgreement: false,
      alreadyAgreed: false,
      maxMainArtists: 4,
      isSpokenWord: false,
      popUpDialogShown: false,
      popUpMainArtistDialogShown: false,
      popUpFeaturingArtistDialogShown: false,
      popUpCopyrightArtistDialogShown: false,
      popUpMyArtistDialogShown: false,
      selectedMainArtistIds: [],
      selectedFeaturingArtistIds: [],
      selectedCopyrightArtistIds: [],
      selectedArtists: [],
      selectedVocaloids: [],
      selectedFeatureVocaloids: [],
      isFeaturing: false,
      productId: 0,
      bus: new Vue(),
      foundVersioWords: false,
      isSelectedUserArtist: false,
      albumArtistIds: [],
      mainArtistWarnErrors: [],
      featArtistWarnErrors: [],
      rightsDocuments: [],
      rightsDocumentsKey: 0,
      nonAffiliatedVocaloidTypeId: -1,
      isRequesting: false,
      pndrIsrcWarning: '',
      isShownPndrIsrcWarning: false,
      roleAssignments: [],
      roleCatalog: {
        performance: [],
        production: [],
      },
      rolePrefillMode: 'manual',
      rolePrefillOptions: [
        { value: 'manual', label: '新しく入力する' },
        { value: 'reuse_last_submission', label: '前回の入力を再利用' },
        { value: 'reuse_previous_release', label: '直近のリリースから再利用' },
        { value: 'reuse_track1', label: '1曲目の情報を引き継ぐ' },
      ],
      roleAssignmentModalType: null,
      roleAssignmentsInitialized: false,
      roleAssignmentModal: {
        isShown: false,
        targetIndex: null,
      },
      roleAssignmentSelectedIds: [],
      roleAssignmentErrors: {},
      performanceRoleModal: {
        isShown: false,
        targetIndex: null,
        selectedIds: [],
      },
      productionRoleModal: {
        isShown: false,
        targetIndex: null,
        selectedIds: [],
      },
      roleAssignmentPerformerGlobalErrors: [],
      roleAssignmentProductionGlobalErrors: [],
      isPerformanceRoleCatalogExpanded: false,
      isProductionRoleCatalogExpanded: false,
      roleAssignmentPrefillError: '',
      isLoadingRoleCatalog: false,
      isLoadingRolePrefill: false,
    }
  },
  computed: {
    performanceRoleAssignments() {
      return this.roleAssignments.filter(
        role => role.inPerformance || role.performanceRoles.length > 0,
      )
    },
    productionRoleAssignments() {
      return this.roleAssignments.filter(
        role => role.inProduction || role.productionRoles.length > 0,
      )
    },
    performanceRoleCatalog() {
      if (!this.roleCatalog.performance) return []
      return this.roleCatalog.performance.map(role => ({
        key: role.key,
        labels: role.labels,
      }))
    },
    productionRoleCatalog() {
      if (!this.roleCatalog.production) return []
      return this.roleCatalog.production.map(role => ({
        key: role.key,
        labels: role.labels,
      }))
    },
    // メインアーティスト選択時に除外するID（Featuringアーティストのみ）
    excludedMainArtistIds() {
      return this.item.featuring_artists
        .filter(artist => artist.id && artist.type !== 3)
        .map(artist => artist.id)
    },
    // Featuringアーティスト選択時に除外するID（メインアーティストのみ）
    excludedFeaturingArtistIds() {
      return this.item.main_artists
        .filter(artist => artist.id && artist.type !== 3)
        .map(artist => artist.id)
    },
    hasNonAffiliatedVocaloidRight() {
      if (this.nonAffiliatedVocaloidTypeId < 0) return false
      return this.rightsDocuments.length > 0
        && this.rightsDocuments.every(
          doc => Number(doc.type) === Number(this.nonAffiliatedVocaloidTypeId),
        )
    },
    disableContentId() {
      let disabled = this.item.classification !== 1
                    || parseInt(this.item.freetrack, 10) !== 0
      if (this.isBtoC) {
        const { nonAffiliatedVocaloidTypeId } = this
        const hasNonAffiliatedVocaloidRight = nonAffiliatedVocaloidTypeId >= 0
          && this.rightsDocuments.length > 0
          && this.rightsDocuments.every(
            doc => Number(doc.type) === Number(nonAffiliatedVocaloidTypeId),
          )
        const useRightsDisabled = parseInt(this.item.use_rights, 10) !== 0
          && !hasNonAffiliatedVocaloidRight
        disabled = disabled
                || parseInt(this.item.useCopyrightFreek, 10) !== 0
                || parseInt(this.item.hasSoldCopyrightFree, 10) !== 0
                || useRightsDisabled
      }
      return !this.item.duration
        ? disabled
        : this.checkDuration() || disabled
    },
    disableContentIdUser() {
      return !this.$store.state.auth.enable_content_id
    },
    disableTitleChange() {
      return this.type === 'single'
    },
    isAppleDevice() {
      return window.navigator.userAgent.indexOf('iPhone') !== -1
        || window.navigator.userAgent.indexOf('iPad') !== -1
        || window.navigator.userAgent.indexOf('Mac') !== -1
    },
    isFirstTrack() {
      return Number(this.discIndex || 0) === 0 && Number(this.trackIndex || 0) === 0
    },
    rolePrefillOptionList() {
      return this.rolePrefillOptions
        .filter(option => this.type === 'album' || option.value !== 'reuse_track1')
        .map(option => ({
          ...option,
          disabled: option.value === 'reuse_track1' && this.isFirstTrack,
        }))
    },
  },
  watch: {
    hasNonAffiliatedVocaloidRight(newVal, oldVal) {
      if (!newVal && oldVal && parseInt(this.item.use_rights, 10) !== 0) {
        // 提携外VOCALOID以外の種類に変更された → YouTubeマネタイズを未選択に戻す
        if (this.checkDuration()) {
          this.enableContentId = 0
        } else {
          this.enableContentId = ''
        }
      }
    },
    enableContentId(value) {
      const restriction = this.getYoutubeRestriction()
      if (!restriction) return
      if (value === false || value === '') {
        if (this.checkDuration()) {
          // 楽曲が30s未満なら利用しないに設定
          this.item.contents_id_enabled = 0
          restriction.settings.contents_id_enabled = 0
        } else {
          // YouTubeマネタイズサービス を未選択の状態にする
          this.item.contents_id_enabled = ''
          restriction.settings.contents_id_enabled = ''
        }
      } else {
        this.item.contents_id_enabled = value > 0 ? 1 : 0
        restriction.settings.contents_id_enabled = value > 0 ? 1 : 0
      }
    },
    'item.is_ai_use': {
      handler(value) {
        if (value !== 0) {
          this.item.use_rights = 1
          if (this.rightsDocuments.length === 0) {
            this.rightsDocuments.push({
              type: 0,
              images: [],
              urls: [],
            })
          }
        } else if (this.item.use_rights === 0) {
          this.$set(this.errors, 'use_rights', [])
          this.rightsDocumentsKey += 1
        }
      },
    },
    'item.freetrack': {
      handler(value) {
        if (value !== 0) {
          if (this.checkDuration()) {
            // 楽曲が30s未満なら利用しないに設定
            this.enableContentId = 0
          } else {
            // YouTubeマネタイズサービス を未選択の状態にする
            this.enableContentId = ''
          }
        }
        const restriction = this.getYoutubeRestriction()
        if (!restriction) return
        restriction.settings.contents_id_confirm.freetrack = value
      },
    },
    'item.use_rights': {
      handler(value) {
        if (value !== 0) {
          if (this.checkDuration()) {
            // 楽曲が30s未満なら利用しないに設定
            this.enableContentId = 0
          } else {
            // YouTubeマネタイズサービス を未選択の状態にする
            this.enableContentId = ''
          }
          this.errors.use_rights = []
        } else if (this.item.is_ai_use === 1) {
          this.errors.use_rights = ['生成AIを利用している場合、「使用している」を選択する必要があります']
        }
      },
    },
    'item.useCopyrightFreek': {
      handler(value) {
        if (value !== 0) {
          if (this.checkDuration()) {
            // 楽曲が30s未満なら利用しないに設定
            this.enableContentId = 0
          } else {
            // YouTubeマネタイズサービス を未選択の状態にする
            this.enableContentId = ''
          }
        }
        const restriction = this.getYoutubeRestriction()
        if (!restriction) return
        restriction.settings.contents_id_confirm.use_copyright_freek = value
      },
    },
    'item.hasSoldCopyrightFree': {
      handler(value) {
        if (value !== 0) {
          if (this.checkDuration()) {
            // 楽曲が30s未満なら利用しないに設定
            this.enableContentId = 0
          } else {
            // YouTubeマネタイズサービス を未選択の状態にする
            this.enableContentId = ''
          }
        }
        const restriction = this.getYoutubeRestriction()
        if (!restriction) return
        restriction.settings.contents_id_confirm.has_sold_copyright_free = value
      },
    },
    errors: {
      handler(value) {
        document.querySelectorAll('.error-input').forEach(input => {
          input.classList.remove('error-input')
        })
        if (Object.keys(value).length) {
          setTimeout(() => {
            const errorText = document.querySelectorAll('.error-text')
            if (errorText) {
              errorText.forEach(text => {
                text.previousElementSibling.classList.add('error-input')
              })
            }
          }, 500)
        }
      },
      deep: true,
    },
  },
  mounted() {
    // セッションストレージから一時保存されたデータを読み込み
    const release = JSON.parse(window.sessionStorage.getItem('album'))
    const productId = release.product.id
    this.productId = release.product.id
    if (!release) {
      this.$router.push({ name: 'index' })
      return
    }
    this.type = release.type
    this.artistType = release.artistType
    // this.maxMainArtists = release.artistType === 2 ? Number.MAX_SAFE_INTEGER : 4
    this.maxMainArtists = Number.MAX_SAFE_INTEGER
    // Various Artist はオムニバスとして登録する
    this.item.is_omnibus = this.artistType === 2
    this.discIndex = this.$route.params.discIndex
    this.releaseArtistsCount = release.artists.length
    const { tracks } = release.discs[this.$route.params.discIndex]
    if (tracks[this.$route.params.trackIndex]) {
      this.item = Object.assign(this.item, tracks[this.$route.params.trackIndex])
      // restrictionsにYoutubeが設定されていたら、contentsIDの設定を取り出す
      const restriction = this.getYoutubeRestriction()
      /* eslint-disable max-len */
      this.item.freetrack = tracks[this.$route.params.trackIndex].freetrack ?? null
      this.item.useCopyrightFreek = tracks[this.$route.params.trackIndex].use_copyright_free ?? tracks[this.$route.params.trackIndex].useCopyrightFreek ?? null
      this.item.hasSoldCopyrightFree = tracks[this.$route.params.trackIndex].has_sold_copyright_free ?? tracks[this.$route.params.trackIndex].hasSoldCopyrightFree ?? null
      this.item.use_copyright_free = tracks[this.$route.params.trackIndex].use_copyright_free ?? tracks[this.$route.params.trackIndex].useCopyrightFreek ?? null
      this.item.has_sold_copyright_free = tracks[this.$route.params.trackIndex].has_sold_copyright_free ?? tracks[this.$route.params.trackIndex].hasSoldCopyrightFree ?? null
      if (restriction) {
        this.enableContentId = restriction.settings.contents_id_enabled !== null && (parseInt(restriction.settings.contents_id_enabled, 10) || 0) === 1
          ? 1 : 0
        if (this.item.freetrack + this.item.useCopyrightFreek + this.item.hasSoldCopyrightFree > 0) {
          if (this.checkDuration()) {
            this.enableContentId = 0
            restriction.settings.contents_id_enabled = 0
          } else {
            this.enableContentId = ''
            restriction.settings.contents_id_enabled = ''
          }
        }
        if (restriction.settings.contents_id_confirm) {
          /* eslint-disable max-len */
          this.item.freetrack = this.item.freetrack != null ? this.item.freetrack : parseInt(restriction.settings.contents_id_confirm.freetrack, 10) || null
          this.item.useCopyrightFreek = this.item.useCopyrightFreek != null ? this.item.useCopyrightFreek : parseInt(restriction.settings.contents_id_confirm.use_copyright_freek, 10) || null
          this.item.hasSoldCopyrightFree = this.item.hasSoldCopyrightFree != null ? this.item.hasSoldCopyrightFree : parseInt(restriction.settings.contents_id_confirm.has_sold_copyright_free, 10) || null
          this.item.use_copyright_free = tracks[this.$route.params.trackIndex].use_copyright_free ?? tracks[this.$route.params.trackIndex].useCopyrightFreek ?? null
          this.item.has_sold_copyright_free = tracks[this.$route.params.trackIndex].has_sold_copyright_free ?? tracks[this.$route.params.trackIndex].hasSoldCopyrightFree ?? null
          /* eslint-enable */
        } else {
          // 古い楽曲には設定されていないので追加する
          /* eslint-disable max-len */
          restriction.settings.contents_id_confirm = {
            freetrack: null,
            use_copyright_freek: null,
            has_sold_copyright_free: null,
          }
          this.item.useCopyrightFreek = null
          this.item.hasSoldCopyrightFree = null
          this.item.use_copyright_free = tracks[this.$route.params.trackIndex].use_copyright_free ?? tracks[this.$route.params.trackIndex].useCopyrightFreek ?? null
          this.item.has_sold_copyright_free = tracks[this.$route.params.trackIndex].has_sold_copyright_free ?? tracks[this.$route.params.trackIndex].hasSoldCopyrightFree ?? null
          /* eslint-enable */
        }
      }
      if (this.enableContentId === '' && this.checkDuration()) {
        this.enableContentId = 0
      }
      this.item.main_artists.some((artist, i) => {
        if (release.artists.indexOf(artist.id) === -1) {
          this.releaseArtistsCount = i
          return true
        }
        return false
      })
      // メインアーティストにVarious Artistsがあったらクリア
      for (let i = 0; i < this.item.main_artists.length; i += 1) {
        if (this.item.main_artists[i].type === 2) {
          this.$set(this.item.main_artists, i, {
            name_ja: '', name_kana: '', name_en: '', type: 1,
          })
        }
      }
      // カナが未入力の著作者(旧著作アーティストの移行データ)は下書きの再編集でも
      // 新規アーティスト扱いにしてカナを入力できるようにし、サーバ保存時に上書きする
      this.item.copyright_artists.forEach(artist => {
        if (artist.id && !artist.name_kana && (artist.type ?? 1) < 3) {
          // eslint-disable-next-line no-param-reassign
          artist.id = null
        }
      })
      this.albumArtistIds = release.artists
    } else {
      // 一時保存されたデータがなければアーティスト情報を読み込み、メインアーティストをデフォルト値としてセットする
      this.fetchProductArtists(productId).then(() => {
        this.item.main_artists = this.artists
        this.item.main_artists.forEach(artist => {
          this.albumArtistIds.push(artist.id)
          if (artist.type === 3) {
            this.selectedVocaloids.push(artist)
          }
        })
        if (this.item.featuring_artists.length > 0) {
          this.item.featuring_artists.forEach(vocloid => {
            if (vocloid.type === 3) {
              this.selectedFeatureVocaloids.push(vocloid)
            }
          })
        }
        this.initializeRoleAssignments()
      })
    }

    this.item.main_artists.forEach(vocaloid1 => {
      if (vocaloid1.type === 3) {
        this.selectedVocaloids.push(vocaloid1)
      }
    })
    if (this.item.featuring_artists.length > 0) {
      this.item.featuring_artists.forEach(vocloid => {
        if (vocloid.type === 3) {
          this.selectedFeatureVocaloids.push(vocloid)
        }
      })
    }
    if (release.type === 'single'
      && tracks[this.$route.params.trackIndex]?.copyright_artists.length === 0
      && release.artists.length > 0) {
      this.fetchArtistsByIds(release.artists)
    }
    if (tracks[this.$route.params.trackIndex]?.copyright_artists.length === 0) {
      const lVocaloid = release.vocaloid || []
      if (lVocaloid.length > 0) {
        release.vocaloid.forEach(vocaloid1 => {
          if (this.item.main_artists.filter(e => e.name_ja === vocaloid1.name_ja).length === 0) {
            this.item.main_artists.push(vocaloid1)
            this.selectedVocaloids.push(vocaloid1)
          }
        })
      }
    }
    this.item.main_artists = this.item.main_artists.filter(artist => artist.name_ja !== '')

    // 楽曲だけ登録して抜けてしまうと楽曲メインアーティストのIDがセットされていない不具合があったので
    // releaseArtistsCountが0の場合は登録されているアルバムメインアーティストで名寄せしてIDをセットする
    if (this.releaseArtistsCount === 0) {
      this.fetchProductArtists(productId).then(() => {
        for (let i = 0; i < release.artists.length; i += 1) {
          if (this.item.main_artists[i]) {
            const result = this.artists.find(
              data => data.name_ja === this.item.main_artists[i].name_ja
                && data.name_kana === this.item.main_artists[i].name_kana
                && data.name_en === this.item.main_artists[i].name_en,
            )
            if (result) {
              this.item.main_artists[i].id = result.id
              this.releaseArtistsCount += 1
            }
          } else {
            break
          }
        }
      })
    }

    // ない場合は仮のデータを作る
    if (!this.getYoutubeRestriction()) {
      // eslint-disable-next-line no-underscore-dangle
      this.item._restrictions.push({
        country: 'WW',
        provider_id: 12,
        start: moment(new Date()).format('YYYY-MM-DD'),
        provider: {
          id: 12,
          key: 'youtube',
        },
        settings: {
          contents_id_enabled: this.checkDuration() ? 0 : '', // 楽曲が30s未満なら利用しないにして、30s以上なら未選択状態
          contents_enabled: 1,
          contents_id_confirm: {
            freetrack: null,
            use_copyright_freek: null,
            has_sold_copyright_free: null,
          },
        },
      })
    }
    // toCでsingleの場合にタイトルを強制セットする
    if (this.type === 'single') {
      this.item.title_ja = release.product.title_ja ?? ''
      this.item.title_en = release.product.title_en ?? ''
      this.item.title_kana = release.product.title_kana ?? ''
      this.item.permission_cover_music = release.product.permission_cover_music ?? ''
      if (release.product.version_ja && this.item.version_ja === '') {
        this.item.version_ja = release.product.version_ja ?? ''
        this.item.version_en = release.product.version_en ?? ''
        this.item.version_kana = release.product.version_kana ?? ''
      }
    }
    if (this.isBtoC) {
      if (this.item.classification === 0) this.item.classification = 1
    }
    // 各種マスタ初期化
    this.fetchLanguages()
    this.fetchCopyrightGroups()
    this.fetchRightsDocumentTypes()
    // バージョンの情報取得したときに中身があるときの処理
    this.versionContentsCheck()
    this.checkTitleInitialHasDigit()
    this.isSpokenWord = (!this.item.id || this.enableContentId === 0) && [release.product.genre1, release.product.genre2].some(genre => genre === 'Spoken Word')

    // YouTube マネタイズの同意
    this.alreadyAgreed = typeof release.product.youtube_monetize_agreed === 'undefined' ? false : release.product.youtube_monetize_agreed
    this.item.youtube_monetize_agreed = typeof release.product.youtube_monetize_agreed === 'undefined' ? false : release.product.youtube_monetize_agreed
    if (this.item.youtube_monetize_agreed) {
      this.readAgreement = true
    }
    // ジャンルにSpoken Wordが選択されていた場合は強制的に「利用しない」をセット
    if (this.isSpokenWord) {
      this.readAgreement = true
      this.item.youtube_monetize_agreed = true
      this.enableContentId = 0
    }
    if (this.$store.state.auth.permission === 10) {
      this.readAgreement = true
      this.item.youtube_monetize_agreed = true
    }
    if (this.item.id
        && (this.item.classification === 2 || this.item.classification === 3)) {
      this.item.permission_cover_music = 1
    }
    if (this.item.classification === 2) {
      this.version_status = true
      this.versionAddedCover = true
    }
    this.rightsDocuments = this.item.rights_documents ?? []

    this.loadRoleCatalog()
    this.initializeRoleAssignments()
  },
  methods: {
    async loadRoleCatalog() {
      if (this.isLoadingRoleCatalog) return
      this.isLoadingRoleCatalog = true
      try {
        const { data } = await window.axios.get('/api/client/role-assignments/catalog')
        this.roleCatalog.performance = Array.isArray(data.performance_roles)
          ? data.performance_roles
          : []
        this.roleCatalog.production = Array.isArray(data.production_roles)
          ? data.production_roles
          : []
      } catch (error) {
        this.roleCatalog.performance = []
        this.roleCatalog.production = []
      } finally {
        this.isLoadingRoleCatalog = false
      }
    },
    async initializeRoleAssignments() {
      this.roleAssignmentsInitialized = false
      this.rolePrefillMode = this.item.role_prefill_mode || 'manual'
      await this.hydrateRoleAssignmentsFromItem()
      // メインアーティストからの自動同期を削除
      // 既存の役割設定があればそれを使用、なければメインアーティストをデフォルトとして設定
      if (!this.roleAssignments.length && this.item.main_artists?.length) {
        // 初回のみメインアーティストをデフォルトとして設定
        // const mainArtist = this.item.main_artists[0]
        // if (mainArtist) {
        //   this.roleAssignments = [this.createRoleAssignmentFromSource({
        //     ...mainArtist,
        //     origin: 'default',
        //   })]
        // }
      }
      this.roleAssignmentsInitialized = true
    },
    async hydrateRoleAssignmentsFromItem() {
      const existing = Array.isArray(this.item.role_assignments) ? this.item.role_assignments : []
      this.roleAssignments = existing.map(
        assignment => this.normalizeRoleAssignmentPayload(assignment),
      )

      // artistIdがあるアーティストの詳細情報を取得
      const artistIds = this.roleAssignments
        .filter(a => a.artistId)
        .map(a => a.artistId)

      if (artistIds.length > 0) {
        const { data } = await window.axios.get('/api/client/artists', {
          params: {
            ids: artistIds.join(','),
            isPaginate: 'false',
          },
        })

        // アーティスト情報をマップに変換
        const artistMap = new Map()
        if (data.data) {
          data.data.forEach(artist => {
            artistMap.set(artist.id, artist)
          })
        } else if (Array.isArray(data)) {
          data.forEach(artist => {
            artistMap.set(artist.id, artist)
          })
        }

        // roleAssignmentsに詳細情報を反映
        this.roleAssignments = this.roleAssignments.map(assignment => {
          if (assignment.artistId && artistMap.has(assignment.artistId)) {
            const artist = artistMap.get(assignment.artistId)
            return {
              ...assignment,
              nameJa: artist.name_ja || artist.name || assignment.nameJa || '',
              nameEn: artist.name_en || assignment.nameEn || '',
              nameKana: artist.name_kana || assignment.nameKana || '',
              type: artist.type || assignment.type || 1,
            }
          }
          return assignment
        })
      }
    },
    normalizeRoleAssignmentPayload(payload = {}) {
      const artistId = payload.artist_id ?? payload.artistId ?? null
      const artistTempUuid = payload.artist_temp_uuid ?? payload.artistTempUuid ?? null
      const performanceRoles = this.normalizeRoleArray(payload.performance_roles)
      const productionRoles = this.normalizeRoleArray(payload.production_roles)
      const productionOptOut = Boolean(
        payload.production_roles_opt_out ?? payload.productionRolesOptOut,
      )
      // 既存データから復元する場合、役割が設定されているセクションに参加していると見なす
      const inPerformance = performanceRoles.length > 0
      const inProduction = productionRoles.length > 0 || productionOptOut
      return {
        key: this.generateTempUuid(),
        artistId,
        artistTempUuid: artistTempUuid || (artistId ? null : this.generateTempUuid()),
        nameJa: payload.name_ja ?? payload.nameJa ?? '',
        nameEn: payload.name_en ?? payload.nameEn ?? '',
        nameKana: payload.name_kana ?? payload.nameKana ?? '',
        type: payload.type ?? 1,
        performanceRoles,
        productionRoles: productionOptOut ? [] : productionRoles,
        productionRolesOptOut: productionOptOut,
        productionRolesOptOutReason: payload.production_roles_opt_out_reason ?? payload.productionRolesOptOutReason ?? '',
        origin: payload.origin ?? 'manual',
        inPerformance,
        inProduction,
      }
    },
    normalizeRoleArray(value) {
      if (Array.isArray(value)) {
        return value.filter(role => typeof role === 'string' && role.trim() !== '')
      }
      if (typeof value === 'string') {
        const trimmed = value.trim()
        if (trimmed === '') return []
        try {
          const parsed = JSON.parse(trimmed)
          if (Array.isArray(parsed)) {
            return parsed.filter(role => typeof role === 'string' && role.trim() !== '')
          }
        } catch (error) {
          // not json, fall through
        }
        return [trimmed]
      }
      return []
    },
    generateTempUuid() {
      return `temp-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
    },
    ensureArtistTempUuid(artist) {
      if (!artist) return null
      if (!artist.temp_uuid) {
        this.$set(artist, 'temp_uuid', this.generateTempUuid())
      }
      return artist.temp_uuid
    },
    // collectRoleAssignmentSourceArtists() {
    //   const sources = []
    //   const process = (artist, origin) => {
    //     if (!artist) return
    //     this.ensureArtistTempUuid(artist)
    //     sources.push({
    //       id: artist.id ?? null,
    //       temp_uuid: artist.temp_uuid ?? null,
    //       name_ja: artist.name_ja ?? '',
    //       name_en: artist.name_en ?? '',
    //       name_kana: artist.name_kana ?? '',
    //       type: artist.type ?? 1,
    //       origin,
    //     })
    //   }
    //   this.item.main_artists.forEach(artist => process(artist, 'main'))
    //   this.item.featuring_artists.forEach(artist => process(artist, 'featuring'))
    //   return sources
    // },
    // resolveArtistKey(entity) {
    //   if (!entity) return null
    //   if (entity.id) {
    //     return `id:${entity.id}`
    //   }
    //   if (entity.temp_uuid) {
    //     return `temp:${entity.temp_uuid}`
    //   }
    //   return `temp:${this.generateTempUuid()}`
    // },
    // resolveRoleAssignmentKey(assignment) {
    //   if (!assignment) return null
    //   if (assignment.artistId) {
    //     return `id:${assignment.artistId}`
    //   }
    //   if (assignment.artistTempUuid) {
    //     return `temp:${assignment.artistTempUuid}`
    //   }
    //   return `key:${assignment.key}`
    // },
    // createRoleAssignmentFromSource(source) {
    //   const tempUuid = source.temp_uuid || this.generateTempUuid()
    //   return {
    //     key: this.generateTempUuid(),
    //     artistId: source.id || null,
    //     artistTempUuid: tempUuid,
    //     nameJa: source.name_ja || '',
    //     nameEn: source.name_en || '',
    //     nameKana: source.name_kana || '',
    //     type: source.type || 1,
    //     performanceRoles: [],
    //     productionRoles: [],
    //     productionRolesOptOut: false,
    //     productionRolesOptOutReason: '',
    //     origin: source.origin,
    //   }
    // },
    serializeRoleAssignment(assignment) {
      const payload = {
        artist_id: assignment.artistId || null,
        artist_temp_uuid: assignment.artistTempUuid || null,
        // 名前フィールドを直接含める（main_artists等と同じ）
        name_ja: assignment.nameJa || '',
        name_kana: assignment.nameKana || '',
        name_en: assignment.nameEn || '',
        type: assignment.type || 1,
        // 役割フィールド
        performance_roles: assignment.performanceRoles || [],
        production_roles: assignment.productionRoles || [],
        production_roles_opt_out: Boolean(assignment.productionRolesOptOut),
        production_roles_opt_out_reason: assignment.productionRolesOptOutReason || null,
      }
      // artist_payloadの生成を完全削除
      return payload
    },
    commitRoleAssignments() {
      this.item.role_assignments = this.roleAssignments.map(
        assignment => this.serializeRoleAssignment(assignment),
      )
      this.item.role_prefill_mode = this.rolePrefillMode
    },
    showPerformanceRoleAssignmentModal() {
      this.roleAssignmentModalType = 'performance'
      this.showRoleAssignmentModal()
    },
    showProductionRoleAssignmentModal() {
      this.roleAssignmentModalType = 'production'
      this.showRoleAssignmentModal()
    },
    showRoleAssignmentModal() {
      this.roleAssignmentModal.isShown = true
      this.roleAssignmentSelectedIds = this.roleAssignmentModalType === 'performance'
        ? this.performanceRoleAssignments.filter(a => a.artistId).map(a => a.artistId)
        : this.productionRoleAssignments.filter(a => a.artistId).map(a => a.artistId)
    },
    closeRoleAssignmentModal() {
      this.roleAssignmentModal.isShown = false
      this.roleAssignmentModal.targetIndex = null
    },
    showPerformanceRoleModal(targetIndex) {
      const assignment = this.performanceRoleAssignments.find((_, idx) => idx === targetIndex)
      if (assignment) {
        this.performanceRoleModal.targetIndex = targetIndex
        this.performanceRoleModal.selectedIds = [...(assignment.performanceRoles || [])]
        this.performanceRoleModal.isShown = true
      }
    },
    closePerformanceRoleModal() {
      this.performanceRoleModal.isShown = false
      this.performanceRoleModal.targetIndex = null
      this.performanceRoleModal.selectedIds = []
    },
    onPerformanceRoleSelect(selectedRoles) {
      const { targetIndex } = this.performanceRoleModal
      const assignment = this.performanceRoleAssignments
        .find((_, idx) => idx === targetIndex)
      if (assignment) {
        assignment.performanceRoles = [...selectedRoles]
        this.onRoleAssignmentUpdated(assignment.key, assignment)
      }
      this.closePerformanceRoleModal()
    },
    showProductionRoleModal(targetIndex) {
      const assignment = this.productionRoleAssignments
        .find((_, idx) => idx === targetIndex)
      if (assignment) {
        this.productionRoleModal.targetIndex = targetIndex
        this.productionRoleModal.selectedIds = [...(assignment.productionRoles || [])]
        this.productionRoleModal.isShown = true
      }
    },
    closeProductionRoleModal() {
      this.productionRoleModal.isShown = false
      this.productionRoleModal.targetIndex = null
      this.productionRoleModal.selectedIds = []
    },
    onProductionRoleSelect(selectedRoles) {
      const { targetIndex } = this.productionRoleModal
      const assignment = this.productionRoleAssignments
        .find((_, idx) => idx === targetIndex)
      if (assignment) {
        assignment.productionRoles = [...selectedRoles]
        this.onRoleAssignmentUpdated(assignment.key, assignment)
      }
      this.closeProductionRoleModal()
    },
    onRoleAssignmentArtistSelect(artists) {
      if (!artists || !artists.length) {
        this.closeRoleAssignmentModal()
        return
      }

      const { targetIndex } = this.roleAssignmentModal
      const isPerformance = this.roleAssignmentModalType === 'performance'
      const roleAssignments = isPerformance
        ? this.performanceRoleAssignments
        : this.productionRoleAssignments

      if (targetIndex !== null) {
        // 既存のアーティストを変更（同セクション内でのアーティスト差し替え）
        const artist = artists[0]
        const existing = roleAssignments[targetIndex]
        const sourceIndex = this.roleAssignments.indexOf(existing)
        if (sourceIndex === -1) {
          this.closeRoleAssignmentModal()
          return
        }
        const updatedAssignment = {
          ...existing,
          artistId: artist.id || null,
          artistTempUuid: artist.temp_uuid || this.generateTempUuid(),
          nameJa: artist.name_ja || artist.name || '',
          nameEn: artist.name_en || '',
          nameKana: artist.name_kana || '',
          type: artist.type || 1,
          inPerformance: isPerformance ? true : Boolean(existing.inPerformance),
          inProduction: !isPerformance ? true : Boolean(existing.inProduction),
        }
        this.$set(this.roleAssignments, sourceIndex, updatedAssignment)
      } else {
        // 新規追加
        artists.forEach(artist => {
          const existingArtists = artist.id
            ? this.roleAssignments.filter(assignment => assignment.artistId === artist.id)
            : []

          if (!existingArtists.length) {
            const newAssignment = {
              key: this.generateTempUuid(),
              artistId: artist.id || null,
              artistTempUuid: artist.temp_uuid || this.generateTempUuid(),
              nameJa: artist.name_ja || artist.name || '',
              nameEn: artist.name_en || '',
              nameKana: artist.name_kana || '',
              type: artist.type || 1,
              performanceRoles: [],
              productionRoles: [],
              productionRolesOptOut: false,
              productionRolesOptOutReason: '',
              origin: 'manual',
              inPerformance: isPerformance,
              inProduction: !isPerformance,
            }
            this.roleAssignments.push(newAssignment)
          } else {
            // 既存エントリに対しては、対象セクションのフラグだけ立てる
            // （もう一方のセクションへの参加状態と役割は維持する）
            const existingArtist = existingArtists[0]
            const index = this.roleAssignments.indexOf(existingArtist)
            const existing = this.roleAssignments[index]
            const updatedAssignment = {
              ...existing,
              inPerformance: isPerformance ? true : Boolean(existing.inPerformance),
              inProduction: !isPerformance ? true : Boolean(existing.inProduction),
            }
            this.$set(this.roleAssignments, index, updatedAssignment)
          }
        })
      }

      this.commitRoleAssignments()
      this.closeRoleAssignmentModal()
    },
    deleteRoleAssignment(key, section = null) {
      const index = this.roleAssignments.findIndex(a => a.key === key)
      if (index === -1) return

      // セクション指定がない場合は従来通り全体削除（後方互換）
      if (!section) {
        this.roleAssignments.splice(index, 1)
        this.commitRoleAssignments()
        this.clearRoleAssignmentErrors(key)
        return
      }

      // 指定されたセクションの参加状態と役割のみクリアし、
      // 両セクションとも非参加かつ役割が空になった場合のみエントリ自体を削除する
      const existing = this.roleAssignments[index]
      const updatedAssignment = { ...existing }
      if (section === 'performance') {
        updatedAssignment.inPerformance = false
        updatedAssignment.performanceRoles = []
      } else if (section === 'production') {
        updatedAssignment.inProduction = false
        updatedAssignment.productionRoles = []
        updatedAssignment.productionRolesOptOut = false
        updatedAssignment.productionRolesOptOutReason = ''
      }

      const remainsInPerformance = updatedAssignment.inPerformance
        || updatedAssignment.performanceRoles.length > 0
      const remainsInProduction = updatedAssignment.inProduction
        || updatedAssignment.productionRoles.length > 0

      if (!remainsInPerformance && !remainsInProduction) {
        this.roleAssignments.splice(index, 1)
      } else {
        this.$set(this.roleAssignments, index, updatedAssignment)
      }

      this.commitRoleAssignments()
      this.clearRoleAssignmentErrors(key)
    },
    onPerformanceRoleAssignmentNewArtistAdd() {
      this.roleAssignmentModalType = 'performance'
      this.onRoleAssignmentNewArtistAdd()
    },
    onProductionRoleAssignmentNewArtistAdd() {
      this.roleAssignmentModalType = 'production'
      this.onRoleAssignmentNewArtistAdd()
    },
    onRoleAssignmentNewArtistAdd() {
      const isPerformance = this.roleAssignmentModalType === 'performance'
      const newArtist = {
        key: this.generateTempUuid(),
        artistId: null,
        artistTempUuid: this.generateTempUuid(),
        nameJa: '',
        nameEn: '',
        nameKana: '',
        type: 1,
        performanceRoles: [],
        productionRoles: [],
        productionRolesOptOut: false,
        productionRolesOptOutReason: '',
        origin: 'manual',
        inPerformance: isPerformance,
        inProduction: !isPerformance,
      }
      this.roleAssignments.push(newArtist)
      this.commitRoleAssignments()
    },
    onRoleAssignmentUpdated(key, payload) {
      const index = this.roleAssignments.findIndex(assignment => assignment.key === key)
      if (index === -1) {
        return
      }

      const previous = this.roleAssignments[index]
      const performanceRoles = this.normalizeRoleArray(
        payload.performanceRoles ?? previous.performanceRoles,
      )
      const productionRoles = this.normalizeRoleArray(
        payload.productionRoles ?? previous.productionRoles,
      )

      const nextAssignment = {
        ...previous,
        ...payload,
        key: previous.key,
        artistId: payload.artistId ?? previous.artistId ?? null,
        artistTempUuid: payload.artistTempUuid
          ?? previous.artistTempUuid
          ?? this.generateTempUuid(),
        performanceRoles,
        productionRoles,
      }

      this.$set(this.roleAssignments, index, nextAssignment)
      this.clearRoleAssignmentErrors(key)

      if (this.rolePrefillMode !== 'manual') {
        this.rolePrefillMode = 'manual'
      }
      this.item.role_prefill_mode = this.rolePrefillMode

      this.commitRoleAssignments()
    },
    clearRoleAssignmentErrors(key) {
      if (this.roleAssignmentErrors[key]) {
        this.$delete(this.roleAssignmentErrors, key)
      }
      if (this.roleAssignmentPerformerGlobalErrors.length) {
        this.roleAssignmentPerformerGlobalErrors = []
      }
      if (this.roleAssignmentProductionGlobalErrors.length) {
        this.roleAssignmentProductionGlobalErrors = []
      }
    },
    partitionRoleAssignmentErrors(errors = {}) {
      const assignmentErrors = {}
      const globalErrors = []
      const remainingErrors = {}

      Object.entries(errors || {}).forEach(([field, messages]) => {
        if (!field.startsWith('role_assignments')) {
          remainingErrors[field] = messages
          return
        }

        const segments = field.split('.')
        const messageList = Array.isArray(messages) ? messages : [messages]

        if (segments.length < 2) {
          globalErrors.push(...messageList)
          return
        }

        const index = Number(segments[1])
        if (Number.isNaN(index) || !this.roleAssignments[index]) {
          globalErrors.push(...messageList)
          return
        }

        const assignmentKey = this.roleAssignments[index].key
        const attributeSegment = segments[2] || 'general'
        // フィールド名のマッピング
        let attribute = attributeSegment.split('[')[0]

        // バックエンドのフィールド名をフロントエンドのフィールド名にマッピング
        if (attribute === 'name_ja') {
          attribute = 'artist_name_ja'
        } else if (attribute === 'name_kana') {
          attribute = 'artist_name_kana'
        } else if (attribute === 'name_en') {
          attribute = 'artist_name_en'
        }

        if (!assignmentErrors[assignmentKey]) {
          assignmentErrors[assignmentKey] = {}
        }

        const current = assignmentErrors[assignmentKey][attribute] || []
        assignmentErrors[assignmentKey][attribute] = current.concat(messageList)
      })

      return {
        assignmentErrors,
        globalErrors,
        remainingErrors,
      }
    },
    // 言語マスタを取得
    async fetchLanguages() {
      const { data } = await window.axios.get('/api/client/code/languages')
      this.languages = data
    },
    async fetchRightsDocumentTypes() {
      const { data } = await window.axios.get('/api/client/rightsDocumentsTypes')
      const types = [{ id: 0, label: '選択してください' }, ...data]
      const nonAffiliatedType = types.find(
        t => t.label === '提携外のVOCALOID™︎やその他音声合成ソフト',
      )
      this.nonAffiliatedVocaloidTypeId = nonAffiliatedType ? nonAffiliatedType.id : -1
    },
    async fetchArtistsByIds(ids) {
      const { data } = await window.axios.get('/api/client/artists', {
        params: { ids },
      })
      data.data.forEach(singleArtist => {
        if (!this.item.main_artists.filter(artist => artist.name_ja
          === singleArtist.name_ja).length > 0) {
          // eslint-disable-next-line no-param-reassign
          // singleArtist.type = null
          const artist = singleArtist
          // メインアーティストにVarious Artistsがあったらクリア
          if (singleArtist.type === 2) {
            this.item.main_artists.push({
              name_ja: '', name_kana: '', name_en: '', type: 1,
            })
          } else {
            this.item.main_artists.push(artist)
          }
        }
      })
    },
    // 著作権団体マスタを取得
    async fetchCopyrightGroups() {
      const { data } = await window.axios.get('/api/client/code/copyright-groups')
      this.copyrightGroups = data
    },
    // アーティスト情報の取得と初期値のセット
    async fetchProductArtists(productId) {
      const { data } = await window.axios.get(`/api/client/products/${productId}/artists`)
      data.forEach(item => {
        if (!this.artists.filter(artist => artist.name_ja === item.name_ja).length > 0) {
          const artist = item
          // アーティストにVarious Artistsがあったらクリア
          if (artist.type === 2) {
            this.artists.push({
              name_ja: '', name_kana: '', name_en: '', type: 1,
            })
          } else {
            this.artists.push(artist)
          }
        }
      })
    },
    // 戻るボタンクリック
    onReturnClicked() {
      const { discIndex, trackIndex } = this.$route.params
      this.$router.push({
        name: `${this.$route.meta.sideMenuId}-step2`,
        hash: `#disc${discIndex}-track${trackIndex}`,
      })
    },
    onFieldChanged(key) {
      /* display warning message if the japanese and alphabet characters
      along with symbols are entered in japanese name input field */
      const regexMainArtists = /^main_artists\.\d+\.name_ja$/
      const regexFeaturingArtists = /^featuring_artists\.\d+\.name_ja$/
      /*
         Hiragana: [\u3040-\u309F]
         Katakana: [\u30A0-\u30FF]
         Kanji: [\u4E00-\u9FAF]
      */
      const japaneseRegex = /[\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FAF]/
      const alphabetRegex = /[A-Za-z]/
      const specialCharactersRegex = /[()（）\\[\]【】<>＜＞『』]/

      if (regexMainArtists.test(key) && this.item.main_artists.length > 0) {
        this.item.main_artists.forEach((artist, idx) => {
          const containsJapanese = japaneseRegex.test(artist.name_ja)
          const containsAlphabet = alphabetRegex.test(artist.name_ja)
          const containsSpecialCharacters = specialCharactersRegex.test(artist.name_ja)
          const errorKey = `main_artists.${idx}.name_ja`

          if ((containsJapanese && containsAlphabet) || containsSpecialCharacters) {
            this.$set(this.mainArtistWarnErrors, errorKey, '山田 太郎（TARO YAMADA）など、翻訳表記を含めることはできません。')
          } else {
            this.$delete(this.mainArtistWarnErrors, errorKey)
          }
        })
      }

      if (regexFeaturingArtists.test(key) && this.item.featuring_artists.length > 0) {
        this.item.featuring_artists.forEach((artist, idx) => {
          const containsJapanese = japaneseRegex.test(artist.name_ja)
          const containsAlphabet = alphabetRegex.test(artist.name_ja)
          const containsSpecialCharacters = specialCharactersRegex.test(artist.name_ja)
          const errorKey = `featuring_artists.${idx}.name_ja`

          if ((containsJapanese && containsAlphabet) || containsSpecialCharacters) {
            this.$set(this.featArtistWarnErrors, errorKey, '山田 太郎（TARO YAMADA）など、翻訳表記を含めることはできません。')
          } else {
            this.$delete(this.featArtistWarnErrors, errorKey)
          }
        })
      }
      if (vTimer) {
        clearTimeout(vTimer)
      }
      vTimer = setTimeout(() => {
        this.onRegisterClicked(key)
      }, 500)
    },
    onScrollAgreement(event) {
      if (!this.readAgreement) {
        if (event.target.scrollTop + event.target.clientHeight >= event.target.scrollHeight - 20) {
          this.readAgreement = true
        }
      }
    },
    compileFormData(confirmed) {
      const keys = [
        'id',
        'title_ja',
        'title_kana',
        'title_en',
        'version_ja',
        'version_kana',
        'version_en',
        'vocal',
        'main_artists',
        'featuring_artists',
        'vocal',
        'is_ai_use',
        'use_rights',
        'freetrack',
        'useCopyrightFreek',
        'hasSoldCopyrightFree',
        'classification',
        'parental_warning',
        'language',
        'copyright_group',
        'copyright_artists',
        'isrc',
        'trial_start_min',
        'trial_start_sec',
        'trial_time_length',
        'remarks_ja',
        'remarks_en',
        'copyright_p_copyright',
        'copyright_p_year',
        'copyright_c_copyright',
        'copyright_c_year',
        'permission_cover_music',
        'companies',
        'monetize_companies',
        'identifiers',
        'identifier_apple',
        'ivk',
        'youtube_monetize_agreed',
        '_restrictions',
        'role_assignments',
        'role_prefill_mode',
      ]
      const params = this.item
      params.confirmed = confirmed
      const formData = new FormData()

      // 再帰的にフォームの要素の追加を行う
      const appendForm = (form, key, value) => {
        if (value == null) {
          form.append(key, '')
        } else if (typeof value === 'object') {
          if (Array.isArray(value)) {
            value.forEach((v, i) => {
              appendForm(form, `${key}[${i}]`, v)
            })
          } else {
            Object.keys(value).forEach(k => {
              appendForm(form, `${key}[${k}]`, value[k])
            })
          }
        } else {
          form.append(key, value)
        }
      }
      Object.keys(params)
        .filter(key => (keys.indexOf(key) !== -1))
        .forEach(key => {
          appendForm(formData, key, params[key])
        })

      // 権利書類の追加
      if (this.item.use_rights === 1 && this.rightsDocuments.length > 0) {
        this.rightsDocuments.forEach((doc, docNum) => {
          formData.append(`rights_documents[${docNum}][type]`, doc.type)
          if (doc.id) {
            formData.append(`rights_documents[${docNum}][id]`, doc.id)
          }
          let counter = 0
          if (doc.showImages) {
            doc.images.forEach((image) => {
              if (!image) return
              if (image.id) {
                formData.append(`rights_documents[${docNum}][saved][${counter}][id]`, image.id)
                counter += 1
              } else if (image.file) {
                formData.append(`rights_documents[${docNum}][files][]`, image.file, image.name)
              }
            })
          }
          if (doc.showUrls) {
            doc.urls.forEach((url) => {
              if (!url) return
              if (url.id) {
                formData.append(`rights_documents[${docNum}][saved][${counter}][id]`, url.id)
                formData.append(`rights_documents[${docNum}][saved][${counter}][path]`, url.path)
                counter += 1
              } else if (url.path) {
                formData.append(`rights_documents[${docNum}][urls][]`, url.path)
              }
            })
          }
        })
      }
      // オリジナル以外の時はいらない情報は削除してから送るようにする
      if (this.item.classification !== 1 && this.item.classification != null) {
        // eslint-disable-next-line no-underscore-dangle
        this.item._restrictions.forEach((value, key) => {
          if (value.provider.key === 'youtube') {
            formData.set(`_restrictions[${key}][settings][contents_id_confirm][freetrack]`, '')
            formData.set(`_restrictions[${key}][settings][contents_id_confirm][use_copyright_freek]`, '')
            formData.set(`_restrictions[${key}][settings][contents_id_confirm][has_sold_copyright_free]`, '')
            formData.set('useCopyrightFreek', '')
            formData.set('hasSoldCopyrightFree', '')
          }
        })
      }

      // 既存の実装に追加したカラムに合わせる
      formData.append('use_copyright_free', formData.get('useCopyrightFreek'))
      formData.append('has_sold_copyright_free', formData.get('hasSoldCopyrightFree'))

      return formData
    },
    // 登録ボタンクリック
    async onRegisterClicked(key = null, validateOnly = 1) {
      if (this.isRequesting) { return }
      this.isRequesting = true
      if (vTimer) {
        clearTimeout(vTimer)
      }
      const config = { headers: { 'X-CMAP-No-Loading': validateOnly, 'content-type': 'multipart/form-data' } }

      const release = JSON.parse(window.sessionStorage.getItem('album'))

      if (key === null) {
        // 試聴開始位置の文字列整形
        this.item.trial_start_min = parseInt(this.item.trial_start_min, 10) || 0
        this.item.trial_start_sec = parseInt(this.item.trial_start_sec, 10) || 0
        // 各アーティストの国内向け、カナ、海外向けが全て空であれば要素を削除する
        this.item.main_artists = this.item.main_artists.filter(
          (artist, index) => (index === 0 || `${artist.name_ja}${artist.name_kana}${artist.name_en}`.length > 0),
        )
        this.item.featuring_artists = this.item.featuring_artists.filter(
          artist => (`${artist.name_ja}${artist.name_kana}${artist.name_en}`.length > 0),
        )
        this.deleteEmptyCopyrightArtist()

        if (this.item.classification !== 1) {
          this.enableContentId = ''
          this.item.freetrack = ''
          this.item.has_sold_copyright_free = ''
          this.item.use_copyright_free = ''
          this.item.hasSoldCopyrightFree = ''
          this.item.useCopyrightFreek = ''
        }
      }
      if (this.disableContentId || this.disableContentIdUser) {
        this.item.contents_id_enabled = this.enableContentId
        const restriction = this.getYoutubeRestriction()
        if (restriction
          && this.item.freetrack !== null
          && this.item.hasSoldCopyrightFree !== null
          && this.item.useCopyrightFreek !== null) {
          const useRightsCount = this.hasNonAffiliatedVocaloidRight ? 0 : this.item.use_rights
          if (this.item.freetrack
            + this.item.useCopyrightFreek
            + this.item.hasSoldCopyrightFree
            + useRightsCount > 0) {
            if (this.checkDuration()) {
              this.enableContentId = 0
            } else {
              this.enableContentId = ''
            }
            restriction.settings.contents_id_enabled = 0
          } else {
            restriction.settings.contents_id_enabled = this.enableContentId
          }
        }
      }

      if (this.item.has_sold_copyright_free !== this.item.hasSoldCopyrightFree) {
        this.item.has_sold_copyright_free = this.item.hasSoldCopyrightFree
      }

      if (this.item.use_copyright_free !== this.item.useCopyrightFreek) {
        this.item.use_copyright_free = this.item.useCopyrightFreek
      }

      const formData = this.compileFormData(key)
      formData.append('validate_only', validateOnly)
      try {
        const { discIndex, trackIndex } = this.$route.params
        const { data } = await window.axios.post(`/api/client/products/${release.product.id}/disc/${this.discIndex}/track/${trackIndex}`, formData, config)
        this.item.rights_documents = this.item.use_rights === 1 ? data.rights_documents : []

        if (!validateOnly) {
          // 本登録時のみsessionStorageに書き込む
          this.item.id = data.id
          const track = release.discs[discIndex].tracks[trackIndex]
          // 保存レスポンスの role_assignments は artist_id とロールのみで名前を含まないため、
          // 入力時の名前情報を artist_id（無ければ同順）で補完し、表示用に保持する
          const savedRoleAssignments = data.meta_artists.role_assignments ?? []
          const localRoleAssignments = this.item.role_assignments ?? []
          const mergedRoleAssignments = savedRoleAssignments.map((saved, idx) => {
            const matched = localRoleAssignments.find(
              local => local.artist_id && saved.artist_id && local.artist_id === saved.artist_id,
            ) || localRoleAssignments[idx] || {}
            return {
              ...saved,
              name_ja: saved.name_ja || matched.name_ja || '',
              name_kana: saved.name_kana || matched.name_kana || '',
              name_en: saved.name_en || matched.name_en || '',
              type: saved.type || matched.type || 1,
            }
          })
          Object.assign(track, this.item, {
            main_artists: data.meta_artists.main_artists,
            featuring_artists: data.meta_artists.featuring_artists,
            copyright_artists: data.meta_artists.copyright_artists,
            role_assignments: mergedRoleAssignments,
          })
          release.product.youtube_monetize_agreed = this.item.youtube_monetize_agreed
          window.sessionStorage.setItem('album', JSON.stringify(release))
          await this.bus.$emit('submit')
          setTimeout(this.onReturnClicked, 500)
        } else {
          // バリデーションのみ → sessionStorageは触らない
          await this.bus.$emit('submit')
          this.errors = {}
        }
      } catch ({ response }) {
        const { errors } = response.data
        if (key && !errors.status) {
          this.realtimeValidation = true
          if (Array.isArray(key)) {
            key.forEach(k => {
              if (k === 'has_sold_copyright_free') {
                this.$set(this.errors, 'hasSoldCopyrightFree', errors[k])
              } else if (k === 'use_copyright_free') {
                this.$set(this.errors, 'useCopyrightFreek', errors[k])
              } else {
                this.$set(this.errors, k, errors[k])
              }
            })
          } else {
            this.$set(this.errors, key, errors[key])
          }
        } else {
          this.realtimeValidation = false

          // role_assignments エラーを分離
          const {
            assignmentErrors,
            globalErrors,
            remainingErrors,
          } = this.partitionRoleAssignmentErrors(errors)

          // role_assignments 以外のエラーを設定
          this.errors = remainingErrors

          // role_assignments エラーを設定
          this.roleAssignmentErrors = assignmentErrors
          this.roleAssignmentPerformerGlobalErrors = globalErrors.slice()
          this.roleAssignmentProductionGlobalErrors = globalErrors.slice()

          if (this.roleAssignmentPerformerGlobalErrors.length === 0
            && this.performanceRoleAssignments.length === 0
            && this.isBtoC
          ) {
            this.roleAssignmentPerformerGlobalErrors.push('パフォーマンスの役割は必須です')
          }

          // プロダクション役割が実際に選択されているかチェック
          // （productionRoleAssignments にアーティストがいても、役割が選択されていない場合がある）
          const hasAnyProductionRoles = this.roleAssignments.some(
            role => role.productionRoles && role.productionRoles.length > 0,
          )

          // バックエンドからの production_roles エラーがあるかチェック
          const productionRolesErrorFromAssignments = Object.values(assignmentErrors).find(
            errs => errs.production_roles && errs.production_roles.length > 0,
          )

          // プロダクション役割が選択されていない場合にエラーを表示
          if (this.roleAssignmentProductionGlobalErrors.length === 0
            && !hasAnyProductionRoles
            && this.isBtoC
          ) {
            if (productionRolesErrorFromAssignments) {
              // バックエンドからのエラーを追加
              this.roleAssignmentProductionGlobalErrors.push(
                productionRolesErrorFromAssignments.production_roles[0],
              )
            } else {
              // フロントエンドのデフォルトエラーを追加
              this.roleAssignmentProductionGlobalErrors.push('プロダクションの役割は必須です')
            }
          }
          const performerAlertErrors = this.roleAssignmentPerformerGlobalErrors.map(
            error => `パフォーマンスの${error}`,
          )
          const productionAlertErrors = this.roleAssignmentProductionGlobalErrors.map(
            error => `プロダクション＆エンジニアリングの${error}`,
          )
          const combinedRoleErrors = [
            ...performerAlertErrors,
            ...productionAlertErrors,
          ]

          if (combinedRoleErrors.length > 0) {
            const uniqueRoleErrors = [...new Set(combinedRoleErrors)]
            this.$set(this.errors, 'role_assignments', uniqueRoleErrors)
          }
          if ('has_sold_copyright_free' in errors) {
            this.errors.hasSoldCopyrightFree = errors.has_sold_copyright_free
          }
          if ('use_copyright_free' in errors) {
            this.errors.useCopyrightFreek = errors.use_copyright_free
          }
        }
        if (!key) {
          window.scrollTo(0, 0)
        }
      }
      this.checkTitleInitialHasDigit()
      this.isRequesting = false
    },
    // バージョン追加クリック
    onVersionAddClicked() {
      this.version_status = true
    },
    // バージョン削除クリック
    onVersionDeleteClicked() {
      this.version_status = false
      this.item.version_ja = null
      this.item.version_kana = null
      this.item.version_en = null
      this.onFieldChanged(['title_ja', 'title_kana', 'title_en'])
    },
    versionContentsCheck() {
      if (this.item.version_ja !== '' && this.item.version_ja !== null) {
        this.version_status = true
      }
    },
    findVersioWords() {
      if (this.item.title_ja.match(/\b(Remix|Remastered|Cover|Live)\b/i) || this.item.title_ja.match(/\bVer\./i)) {
        this.foundVersioWords = true
      } else {
        this.foundVersioWords = false
      }
    },
    // メインアーティスト追加クリック
    onMainArtistAddClicked() {
      this.item.main_artists.push({
        name_ja: '',
        name_kana: '',
        name_en: '',
        type: 1,
      })
    },
    // 配列からのindex指定削除に伴い、削除位置より後ろのエラーキーを1つ前へ詰め替える
    // (splice後は要素のindexが1つずつ繰り上がるが、キーが古いindexのままだと
    //  無関係な要素にエラーが誤って表示され続けてしまうため)
    reindexErrorsAfterDelete(prefix, deletedIndex, ...errorObjects) {
      const pattern = new RegExp(`^${prefix}\\.(\\d+)\\.(.+)$`)
      errorObjects.forEach(errorObject => {
        if (!errorObject) return
        const shifted = []
        Object.keys(errorObject).forEach(key => {
          const matched = key.match(pattern)
          if (!matched) return
          const idx = Number(matched[1])
          const rest = matched[2]
          if (idx === deletedIndex) {
            this.$delete(errorObject, key)
          } else if (idx > deletedIndex) {
            shifted.push({
              idx, oldKey: key, newKey: `${prefix}.${idx - 1}.${rest}`, value: errorObject[key],
            })
          }
        })
        // idxの昇順で処理しないと、あるエントリのnewKeyが未処理の別エントリの
        // oldKeyと衝突し、後者の値を消してしまう(Object.keysの挿入順に依存するバグになる)ため、
        // 必ず数値としてのidxで昇順ソートしてから詰め替える
        shifted
          .sort((a, b) => a.idx - b.idx)
          .forEach(({ oldKey, newKey, value }) => {
            this.$delete(errorObject, oldKey)
            this.$set(errorObject, newKey, value)
          })
      })
    },
    // メインアーティスト削除クリック
    onMainArtistDeleteClicked(index) {
      this.item.main_artists.splice(index, 1)
      if (index < this.releaseArtistsCount) this.releaseArtistsCount -= 1
      this.reindexErrorsAfterDelete('main_artists', index, this.errors, this.mainArtistWarnErrors)
    },
    onVocaloidDeleteClicked(index, vocaloid) {
      const vocaloidIndex = this.selectedVocaloids.indexOf(vocaloid)
      this.item.main_artists.splice(index, 1)
      this.selectedVocaloids.splice(vocaloidIndex, 1)
      this.reindexErrorsAfterDelete('main_artists', index, this.errors, this.mainArtistWarnErrors)
    },
    onFeatureVocaloidDeleteClicked(index, vocaloid) {
      const vocaloidIndex = this.selectedFeatureVocaloids.indexOf(vocaloid)
      this.item.featuring_artists.splice(index, 1)
      this.selectedFeatureVocaloids.splice(vocaloidIndex, 1)
      this.reindexErrorsAfterDelete('featuring_artists', index, this.errors, this.featArtistWarnErrors)
    },
    // Featuringアーティスト追加クリック
    onFeaturingArtistAddClicked() {
      this.item.featuring_artists.push({
        prefix: '3', // クライアント画面からのFeaturing接続表記は「feat.」固定
        name_ja: '',
        name_kana: '',
        name_en: '',
        type: 1,
      })
    },
    // Featuringアーティスト削除クリック
    onFeaturingArtistDeleteClicked(index) {
      this.item.featuring_artists.splice(index, 1)
      this.reindexErrorsAfterDelete('featuring_artists', index, this.errors, this.featArtistWarnErrors)
    },
    // 著作権団体追加ボタンクリック
    onCopyrightArtistAddClicked() {
      this.item.copyright_artists.push({
        roles: this.item.copyright_artists.length === 0 ? ['composer'] : [],
        name_ja: '',
        name_kana: '',
        name_en: '',
      })
    },
    // 著作権団体削除ボタンクリック
    onCopyrightArtistDeleteClicked(index) {
      this.item.copyright_artists.splice(index, 1)
      this.reindexErrorsAfterDelete('copyright_artists', index, this.errors)
    },
    onChangeWithOutVocal() {
      // 作詞のチェックを外す
      const { discIndex, trackIndex } = this.$route.params
      const LyricsErrors = JSON.parse(window.sessionStorage.getItem('hasLyricsError') || '{}')
      LyricsErrors[`${discIndex}-${trackIndex}`] = false
      window.sessionStorage.setItem('hasLyricsError', JSON.stringify(LyricsErrors))
      // 歌詞完了率をクリア（登録時にバックエンドで歌詞が削除されるため）
      const LyricsCompletePercentages = JSON.parse(
        window.sessionStorage.getItem('lyricsCompletePercentage') || '{}',
      )
      delete LyricsCompletePercentages[`${discIndex}-${trackIndex}`]
      window.sessionStorage.setItem('lyricsCompletePercentage', JSON.stringify(LyricsCompletePercentages))
      this.item.copyright_artists.forEach((artist, index) => {
        this.item.copyright_artists[index].roles = artist.roles.filter(
          role => role !== 'lyricist',
        )
      })
      // コピーライトアーティストがからの時は削除
      this.deleteEmptyCopyrightArtist()
      // ボーカル入り「はい」の時に表示された「作詞者を入力してください」エラーが
      // 「いいえ」に変更しても残り続けるため、即座にクリアした上で再検証する
      this.$delete(this.errors, 'copyright_artists.0.roles')
      this.onFieldChanged('copyright_artists.0.roles')
    },
    onChangeWithVocal() {
      const { discIndex, trackIndex } = this.$route.params
      const LyricsCompletePercentages = JSON.parse(window.sessionStorage.getItem('lyricsCompletePercentage') || '{}')
      if (LyricsCompletePercentages !== {}
          && LyricsCompletePercentages[`${discIndex}-${trackIndex}`] !== null
          && LyricsCompletePercentages[`${discIndex}-${trackIndex}`] !== 100) {
        const LyricsErrors = JSON.parse(window.sessionStorage.getItem('hasLyricsError') || '{}')
        LyricsErrors[`${discIndex}-${trackIndex}`] = true
        window.sessionStorage.setItem('hasLyricsError', JSON.stringify(LyricsErrors))
      }
    },
    deleteEmptyCopyrightArtist() {
      this.item.copyright_artists = this.item.copyright_artists.filter(
        artist => (`${artist.name_ja}${artist.name_kana}${artist.name_en}`.length > 0 || artist.roles.length > 0),
      )
    },
    getYoutubeRestriction() {
      // eslint-disable-next-line no-underscore-dangle
      const restrictions = this.item._restrictions
      if (restrictions.length) {
        return restrictions.find(item => item.provider.key === 'youtube')
      }
      return false
    },
    durationToSeconds(duration) {
      if (typeof duration === 'string') {
        const [hour, min, sec] = duration.split(':')
        return parseInt(sec, 10) + parseInt(min, 10) * 60 + parseInt(hour, 10) * 60 * 60
      }
      // 楽曲のデータが登録されていない時はdurationがnullなので0を返す
      return 0
    },
    checkTitleInitialHasDigit() {
      this.hasInitialDigit = this.item.title_ja.match(/^[0-9０-９]/)
    },
    isShowMainArtistDeleteButton(artistId = null) {
      const isVocaloid = this.item.main_artists.find((artist) => artist.type === 3
          && artist.id === artistId)
      const hasRequireUserArtistVocaloid = this.item.main_artists
        .find((artist) => artist.require_user_artist === 1)
      const isAlbumArtist = this.albumArtistIds.find(ids => ids === artistId)
      // ユーザーアーティスト必須のボカロがある場合に残り一つのユーザーアーティストの場合削除させない
      if (!isVocaloid
          && hasRequireUserArtistVocaloid
          && this.item.main_artists.filter(artist => artist.type !== 3).length === 1) {
        return false
      }
      // アルバム選択アーティストの場合に残り一つのアルバム選択アーティストの場合に削除させない
      if (isAlbumArtist
          && this.item.main_artists
            .filter(artist => this.albumArtistIds
              .find(ids => ids === artist.id)).length === 1) {
        return false
      }

      // メインアーティストが残り１つの場合は削除ボタンは非表示(Various Artistsで有効になる)
      if (this.item.main_artists.length === 1) {
        return false
      }
      return true
    },
    showVocaloidListPopUp(isFeaturing = false) {
      this.isSelectedUserArtist = this.item.main_artists.some(artist => artist.type === 1)
      if (isFeaturing) {
        this.isFeaturing = true
      }
      this.popUpDialogShown = true
    },
    closeModel() {
      this.popUpDialogShown = false
    },

    modalConfirm(event) {
      if (this.isFeaturing) {
        event.forEach(e => {
          if (this.selectedFeatureVocaloids.filter(vocaloid => vocaloid.id === e.id).length === 0) {
            e.prefix = '3'
            e.type = 3
            this.selectedFeatureVocaloids.push(e)
            this.item.featuring_artists.push(e)
          }
        })
      } else {
        event.forEach(e => {
          if (this.selectedVocaloids.filter(vocaloid => vocaloid.id === e.id).length === 0) {
            e.type = 3
            this.selectedVocaloids.push(e)
            this.item.main_artists.push(e)
          }
        })
      }
      this.isFeaturing = false
      this.popUpDialogShown = false
    },
    itemToBeDeleted(event) {
      if (this.isFeaturing) {
        event.forEach(element => {
          this.selectedFeatureVocaloids.forEach(element1 => {
            if (element1.id === element.id) {
              const index = this.selectedFeatureVocaloids.indexOf(element1)
              const index1 = this.item.featuring_artists.indexOf(element1)
              this.selectedFeatureVocaloids.splice(index, 1)
              this.item.featuring_artists.splice(index1, 1)
            }
          })
        })
      } else {
        event.forEach(element => {
          this.selectedVocaloids.forEach(element1 => {
            if (element1.id === element.id) {
              const index = this.selectedVocaloids.indexOf(element1)
              const index1 = this.item.main_artists.indexOf(element1)
              this.selectedVocaloids.splice(index, 1)
              this.item.main_artists.splice(index1, 1)
            }
          })
        })
      }
    },
    // メインアーティスト選択モーダルを表示
    showMainArtistPopUp() {
      // 現在のメインアーティストのIDを取得
      this.selectedMainArtistIds = this.item.main_artists
        .filter(artist => artist.id)
        .map(artist => artist.id)
      this.popUpMainArtistDialogShown = true
    },
    // メインアーティスト選択モーダルを閉じる
    closeMainArtistModal() {
      this.popUpMainArtistDialogShown = false
    },
    // Featuringアーティスト選択モーダルを表示
    showFeaturingArtistPopUp() {
      // 現在のFeaturingアーティストのIDを取得
      this.selectedFeaturingArtistIds = this.item.featuring_artists
        .filter(artist => artist.id)
        .map(artist => artist.id)
      this.popUpFeaturingArtistDialogShown = true
    },
    // Featuringアーティスト選択モーダルを閉じる
    closeFeaturingArtistModal() {
      this.popUpFeaturingArtistDialogShown = false
    },
    // Featuringアーティスト選択時の処理
    onFeaturingArtistSelect(artists) {
      if (!artists || artists.length === 0) return

      // 削除されたアーティストを処理
      this.item.featuring_artists = this.item.featuring_artists.filter(
        artist => artist.type >= 3 // ボーカロイドは保持
        || !artist.id // IDのない手入力アーティストは保持
        || artists.some(a => a.id && a.id === artist.id),
      )

      // 選択したアーティストごとに処理
      artists.forEach(artist => {
        // すでに追加済みのアーティストかチェック
        const existingIndex = artist.id
          ? this.item.featuring_artists.findIndex(
            existingArtist => existingArtist.id === artist.id,
          )
          : -1

        // 新規アーティストの場合、Featuringアーティストに追加
        if (existingIndex === -1) {
          const newArtist = {
            id: artist.id,
            name_ja: artist.name_ja || artist.name,
            name_kana: artist.name_kana || '',
            name_en: artist.name_en || '',
            image_path: artist.image_path || '',
            type: artist.type || 1,
            prefix: '3', // クライアント画面からのFeaturing接続表記は「feat.」固定
          }
          this.item.featuring_artists.push(newArtist)
        }
      })
    },
    // 著作者公表名選択モーダルを表示
    showCopyrightArtistPopUp() {
      // 現在の著作者公表名のIDを取得
      this.selectedCopyrightArtistIds = this.item.copyright_artists
        .filter(artist => artist.id)
        .map(artist => artist.id)
      this.popUpCopyrightArtistDialogShown = true
    },
    // 著作者公表名選択モーダルを閉じる
    closeCopyrightArtistModal() {
      this.popUpCopyrightArtistDialogShown = false
    },
    // 著作者公表名選択時の処理
    onCopyrightArtistSelect(artists) {
      if (!artists || artists.length === 0) return

      // 削除されたアーティストを処理
      this.item.copyright_artists = this.item.copyright_artists.filter(
        artist => artist.type >= 3 // ボーカロイドは保持
          || !artist.id // IDのない手入力アーティストは保持
          || artists.some(a => a.id && a.id === artist.id),
      )

      // 選択したアーティストごとに処理
      artists.forEach(artist => {
        // すでに追加済みのアーティストかチェック
        const existingIndex = artist.id
          ? this.item.copyright_artists.findIndex(
            existingArtist => existingArtist.id === artist.id,
          )
          : -1

        // 新規アーティストの場合、著作者公表名に追加
        if (existingIndex === -1) {
          const newArtist = {
            id: artist.id,
            type: artist.type || 1,
            name_ja: artist.name_ja || artist.name,
            name_kana: artist.name_kana || '',
            name_en: artist.name_en || '',
            roles: this.item.copyright_artists.length === 0 ? ['composer'] : [], // 最初の場合は作曲者にデフォルト
          }
          this.item.copyright_artists.push(newArtist)
        }
      })
    },
    // メインアーティスト選択時の処理
    onMainArtistSelect(artists) {
      if (!artists || artists.length === 0) return

      // 削除されたアーティストを処理
      this.item.main_artists = this.item.main_artists.filter(
        artist => artist.type >= 3 // ボーカロイドは保持
        || !artist.id // IDのない手入力アーティストは保持
        || artists.some(a => a.id && a.id === artist.id),
      )

      // 選択したアーティストごとに処理
      artists.forEach(artist => {
        // すでに追加済みのアーティストかチェック
        const existingIndex = artist.id
          ? this.item.main_artists.findIndex(
            existingArtist => existingArtist.id === artist.id,
          )
          : -1

        // 新規アーティストの場合、メインアーティストに追加
        if (existingIndex === -1) {
          const newArtist = {
            id: artist.id,
            name_ja: artist.name_ja || artist.name,
            name_kana: artist.name_kana || '',
            name_en: artist.name_en || '',
            image_path: artist.image_path || '',
            type: artist.type || 1,
          }
          this.item.main_artists.push(newArtist)
        }
      })
    },
    onChangeClassification(value) {
      if (value !== 2) {
        if (this.versionAddedCover) {
          this.item.version_ja = (this.item.version_ja.replace('カバー', '')).trim()
          this.item.version_kana = this.item.version_kana.replace('カバー', '')
          this.item.version_en = (this.item.version_en.replace('cover', '')).trim()
          this.versionAddedCover = false
          if (this.item.version_ja === '') this.version_status = false
        }
        this.enableContentId = ''
      } else if (this.item.version_ja === null) {
        this.item.version_ja = 'カバー'
        this.item.version_kana = 'カバー'
        this.item.version_en = 'cover'
        this.version_status = true
        this.versionAddedCover = true
      } else if (this.item.version_ja.indexOf('カバー') === -1 && this.item.version_ja.toLowerCase().indexOf('cover') === -1) {
        this.item.version_ja = (`${this.item.version_ja} カバー`).trim()
        if (this.item.version_kana.indexOf('カバー') === -1) this.item.version_kana += 'カバー'
        if (this.item.version_en.indexOf('cover') === -1) this.item.version_en = (`${this.item.version_en} cover`).trim()
        this.version_status = true
        this.versionAddedCover = true
      }
      // カバーアレンジの場合に権利書類設定
      if ([2, 3].includes(value)) {
        this.item.permission_cover_music = 1
        this.item.use_rights = 1
        if (this.rightsDocuments.length === 0) {
          this.rightsDocuments.push({
            type: 0,
            images: [],
            urls: [],
          })
        }
      } else {
        // それ以外の時は「カバー・アレンジ許諾」をクリア
        this.item.permission_cover_music = ''
      }
      this.onFieldChanged('classification')
    },
    checkUseRights(value) {
      if (value !== 0) {
        // YouTubeマネタイズサービス を未選択の状態にする
        this.enableContentId = ''
      }
      this.item.use_rights = value
    },
    checkDuration() {
      const YOUTUBE_CONTENT_DURATION_THRESHOLD = 29 // 秒数
      return this.durationToSeconds(this.item.duration) <= YOUTUBE_CONTENT_DURATION_THRESHOLD
    },
    async handleIsrcChange() {
      if (this.appName !== 'PNDR') {
        return
      }
      const release = JSON.parse(window.sessionStorage.getItem('album'))
      const { isrc } = this.item

      const isrcRegex = /^[A-Z]{2}[A-Z0-9]{3}\d{7}$/

      if (!isrc || !isrcRegex.test(isrc)) {
        return
      }

      try {
        await window.axios.post('/api/client/products/validate/isrc', {
          product_id: release.product.id,
          isrc,
        })
      } catch (error) {
        if (error.response && error.response.status === 422) {
          const { errors } = error.response.data
          const message = errors.isrc[0]
          this.pndrIsrcWarning = message
          this.isShownPndrIsrcWarning = true
        }
      }
    },
  },
}
</script>

<style scoped>
@media screen and (max-width: 895px) {
  .c-btn--right {
    margin-top: 0px;
    margin-bottom: 30px;
  }

  .form-group-wrap__btn-wrap {
    height: 120px;
  }
}

@media screen and (max-width: 895px) {
  .form-group-wrap__vocalo-btn-wrap {
    height: 160px;
    margin: 18px 0 0 0;
  }
  .form-group-wrap__vocalo-btn-wrap >.c-btn {
    margin: 14px 0 0 0;
  }
}

@media screen and (min-width: 896px) {
  .copyright-form-control {
    width: 80%;
  }
}

.required-composr::after {
  background-color: #c0c0c0;
}

@media screen and (min-width: 896px) {
  .not-first-space:first-child {
    padding-top: 0px;
  }
}

.agree-area {
  width: 100%;
  height: 300px;
  overflow-y: scroll;
  border: 1px inset #808080;
  background-color: white;
  margin: auto auto;
  padding: 5px 2px 5px 2px;
  text-align: left;
}

.agree-area A {
  text-decoration: underline;
}

.agree-area A:hover {
  text-decoration: none;
}

.asterisk {
  color: #ff4b6d;
  font-weight: bold;
}

LI.list {
  margin-left: 25px;
  padding-left: -15px;
  list-style-type: disc;
}

.apple ::-webkit-scrollbar {
  -webkit-appearance: none;
  width: 8px;
}

.apple ::-webkit-scrollbar-thumb {
  border-radius: 4px;
  background-color: rgba(0, 0, 0, .5);
  box-shadow: 0 0 1px rgba(255, 255, 255, .5);
}
.vocaloid-image-container{
  width: 160px;
  display: flex;
  justify-content: center;
}
.disabled{
  opacity: 0.5;
}

.role-assignment__controls {
  margin-bottom: 16px;
}

.role-assignment__add-button {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background-color: #fff;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.role-assignment__add-button:hover {
  background-color: #f5f5f5;
}

.role-assignment__empty {
  padding: 20px;
  background-color: #f5f5f5;
  border-radius: 4px;
  text-align: center;
  color: #666;
  margin-bottom: 16px;
}

.role-assignment__prefill {
  margin-bottom: 16px;
}

.role-assignment__prefill-control {
  display: flex;
  align-items: center;
  gap: 12px;
}

.role-assignment__prefill-status {
  color: #666;
  font-size: 14px;
}

.form-item-text--collapsible {
  display: inline-flex;
  align-items: flex-start;
  gap: 8px;
}

@media screen and (max-width: 895px) {
  .role-catalog-preview {
    flex: 1;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    line-height: 1.5;
    width: 200px!important;
  }
  .sp-button {
    text-wrap: nowrap;
    padding-left: 8px
  }
}

@media screen and (min-width: 896px) {
  .role-catalog-preview {
    flex: 1;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    line-height: 1.5;
    width: 640px!important;
  }
}

.role-catalog-preview--expanded {
  white-space: normal;
  overflow: visible;
  width: auto!important;
}

.role-catalog-toggle {
  flex-shrink: 0;
  background: #ffffff;
  border: 1px solid #000000;
  border-radius: 10px;
  height: 20px;
  color: #000000;
  cursor: pointer;
  font-size: 12px;
  padding: 0 8px;
  white-space: nowrap;
}

.role-catalog-toggle:hover {
  text-decoration: underline;
}
</style>
